import Add from '@mui/icons-material/Add';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import Star from '@mui/icons-material/Star';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import StarBorder from '@mui/icons-material/StarBorder';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  // CircularProgress,
  Divider,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AISolutionId,
  IAISolution,
  IAllAgents,
  IEnhancedAISolution,
  IMutationAISolution,
} from '../../common/types/common.ts';
import { useGraphQLMutation } from '../../customHooks/useGraphQL.ts';
import { Alert, Snackbar } from '@mui/material';
import Loader from '../../common/components/Loader.tsx';
import Modal from '../../common/components/Modal/Modal.tsx';
import { getBenefitLabelNew, getBenefitTitle } from '../../common/utils.tsx';
import { API_BASE, ENVIRONMENT_TYPE } from '../../config/env.ts';
import { httpConstants } from '../../constants/index.ts';
import { UPDATE_FAVORITE_AI_SOLUTIONS } from '../../graphql/mutations/common.ts';
import { httpService } from '../../utility/httpService.ts';
import SolutionEditModal from './SolutionEditModal.tsx';
import { getEnterpriseTechnology } from '../../services/commonService.tsx';
import ConfirmationModal from '../../common/components/ConfirmationModal.tsx';
import {
  fetchAISolutionByID,
  fetchAISolutionWithAgentEnablement,
} from '../../utility/aiOpportunityUtils.ts';
import { ORCHESTRATOR_CONFIG } from '../../config/orchestratorConfig.ts';
import { createLabelWithUsername } from '../../utility/userUtils.ts';
import TopBenefits from './TopBenefits.tsx';
import { useNavigate } from 'react-router-dom';

interface ExtraAgentData {
  agent_name: string;
  implementation_type: string;
  justification: string;
}

interface CardData {
  capability_name: string;
  description: string;
}

// ---------- AI SOLUTION → DATA SOURCE TECHNOLOGY MAPPING ----------

// type AISolutionAsset = {
//   data_asset: string;
//   technology: string[];
// };

// const normalizeAISolutionAssets = (raw: any[]): AISolutionAsset[] => {
//   if (!Array.isArray(raw)) return [];

//   return raw
//     .map((item) => {
//       if (!item?.data_asset) return null;

//       return {
//         data_asset: String(item.data_asset).trim().toLowerCase(),
//         technology: Array.isArray(item.technology)
//           ? item.technology
//           : [],
//       };
//     })
//     .filter(Boolean) as AISolutionAsset[];
// };

// Helper function to check if sync is needed based on timestamp comparison
const checkIfSyncNeeded = async (enterpriseTechResponse: any, agent: any): Promise<boolean> => {
  try {
    // Check if agent timestamp exists
    if (!agent?.updated_at) {
      return true;
    }

    // Check if enterprise tech timestamp exists
    if (!enterpriseTechResponse?.updated_at) {
      return false;
    }

    // Parse timestamps - both come without timezone info, treat as UTC
    // agent.updated_at
    const agentTimestamp = agent.updated_at.endsWith('Z')
      ? agent.updated_at
      : agent.updated_at + 'Z';
    const agentDate = new Date(agentTimestamp);
    const agentUpdatedAt = agentDate.getTime();

    // enterpriseTechResponse.updated_at
    const techTimestamp = enterpriseTechResponse.updated_at.endsWith('Z')
      ? enterpriseTechResponse.updated_at
      : enterpriseTechResponse.updated_at + 'Z';
    const techDate = new Date(techTimestamp);
    const techUpdatedAt = techDate.getTime();

    // Check if Enterprise Technology updated_at is greater than Agent updated_at
    if (techUpdatedAt > agentUpdatedAt) {
      console.log('🔄 Sync needed: Enterprise Technology is newer than Agent');
      return true;
    }

    return false;
  } catch (error) {
    console.error('Error comparing timestamps:', error);
    // On error, default to NOT syncing to prevent loops
    return false;
  }
};

// Helper function to build sync payload (shared logic)
const buildSyncPayload = (
  aiSolutionData: any,
  enterpriseTechResponse: any,
  additionalPayload?: any,
  processType?: 'custom' | 'standard' | string
) => {
  const { company_name, description, industry_data } = enterpriseTechResponse;
  const {
    id,
    roi,
    impact,
    benefits,
    complexity,
    data_sources,
    key_requirements,
    opportunity_name,
  } = aiSolutionData || {};

  const basePayload = {
    company_info: {
      company_name,
      description,
    },
    technology: industry_data,
    aiSolution: {
      solution_id: id,
      roi,
      impact,
      benefits,
      complexity,
      data_sources,
      key_requirements,
      opportunity_name,
      description: aiSolutionData?.description || '',
    },
    is_from_process_xplr:
      window.location.pathname.includes('process-xplr') && processType === 'standard',
    label: createLabelWithUsername(aiSolutionData?.id || ''),
    organization_id: window?.localStorage?.getItem('current_organization') || '',
  };

  // Merge with additional payload if provided
  return additionalPayload
    ? {
      ...basePayload,
      ...additionalPayload,
      llm_agent: aiSolutionData?.llm_agent,
      solution_enabler: aiSolutionData?.solution_enabler,
      diagram_id: aiSolutionData?.diagram_id,
      organization_id: localStorage.getItem('current_organization') || '',
      type: ENVIRONMENT_TYPE,
    }
    : {
      ...basePayload,
      llm_agent: aiSolutionData?.llm_agent,
      solution_enabler: aiSolutionData?.solution_enabler,
      diagram_id: aiSolutionData?.diagram_id,
      organization_id: localStorage.getItem('current_organization') || '',
      type: ENVIRONMENT_TYPE,
    };
};

// Merge with additional payload if provided
// const payload = additionalPayload
//   ? { ...basePayload, ...additionalPayload }
//   : {
//       ...basePayload,
//       llm_agent: aiSolutionData?.llm_agent,
//       solution_enabler: aiSolutionData?.solution_enabler,
//       diagram_id: aiSolutionData?.diagram_id,
//       organization_id: localStorage.getItem('current_organization') || '',
//       type: ENVIRONMENT_TYPE,
//       label: createLabelWithUsername(aiSolutionData?.id || ''),
//     };

const AGENT_URL_1 = ORCHESTRATOR_CONFIG.ENDPOINTS.AGENT_CLASSIFICATION;
const AGENT_URL_2 = ORCHESTRATOR_CONFIG.ENDPOINTS.AGENT_AI_ENABLEMENT;
// Helper function to merge sync responses
const mergeSyncResponses = (firstResponse: any, secondResponse: any): any => {
  return {
    ...firstResponse,
    ...secondResponse,
    // Properly merge ai_enablement objects
    ai_enablement: {
      ...(typeof firstResponse.ai_enablement === 'object' && firstResponse.ai_enablement
        ? firstResponse.ai_enablement
        : {}),
      ...(typeof secondResponse.ai_enablement === 'object' && secondResponse.ai_enablement
        ? secondResponse.ai_enablement
        : {}),
    },
    // Combine agent_classification arrays
    agent_classification: [
      ...(Array.isArray(firstResponse.agent_classification)
        ? firstResponse.agent_classification
        : []),
      ...(Array.isArray(secondResponse.agent_classification)
        ? secondResponse.agent_classification
        : []),
    ],
    // Combine application_in_use arrays
    application_in_use: [
      ...(Array.isArray(firstResponse.application_in_use) ? firstResponse.application_in_use : []),
      ...(Array.isArray(secondResponse.application_in_use)
        ? secondResponse.application_in_use
        : []),
    ],
  };
};

// Function to sync data sources (AGENT_URL_1)
const makeDataSourcesSyncCall = async (
  aiSolutionData: any,
  enterpriseTechResponse: any,
  additionalPayload?: any,
  processType?: 'custom' | 'standard' | string
): Promise<any> => {
  try {
    const payload = buildSyncPayload(
      aiSolutionData,
      enterpriseTechResponse,
      additionalPayload,
      processType
    );

    // const AGENT_URL_1 =
    //   'https://orchestrator.app.zbrain.ai/api/v1/webhooks/qtD4OqGQNyRHYPpizv6Yu/sync?tenantId=6764029052789d00255f17a1';

    // const headers = {
    //   'Content-Type': 'application/json',
    //   Authorization: `Bearer za-U2FsdGVkX1/ttZ/pUsCjolOeYbHP4uWJKYu0O860+8yYE6ebW6DvrABLSug5Qkw4DIVv+2HmxvP3kq9t+WtL3P4Wt1KaaAc43piiuclp840Ek1C9ibGtQGD44w9fTXuK`,
    // };

    const response = await httpService<any>(
      httpConstants.METHOD_TYPE.POST,
      AGENT_URL_1,
      payload,
      undefined,
      true
    );

    return response;
  } catch (error) {
    console.error('Error making data sources sync call:', error);
    throw error;
  }
};

// Function to sync AI enablement (AGENT_URL_2)
const makeAiEnablementSyncCall = async (
  aiSolutionData: any,
  enterpriseTechResponse: any,
  additionalPayload?: any,
  processType?: 'custom' | 'standard' | string
): Promise<any> => {
  try {
    const payload = buildSyncPayload(
      aiSolutionData,
      enterpriseTechResponse,
      additionalPayload,
      processType
    );

    // const AGENT_URL_2 =
    //   'https://orchestrator.zbrain.ai/api/v1/webhooks/EaqsB7pt8hBFF82dB8nnX/sync?tenantId=6764029052789d00255f17a1';

    // const headers = {
    //   'Content-Type': 'application/json',
    //   Authorization: `Bearer za-U2FsdGVkX1/ttZ/pUsCjolOeYbHP4uWJKYu0O860+8yYE6ebW6DvrABLSug5Qkw4DIVv+2HmxvP3kq9t+WtL3P4Wt1KaaAc43piiuclp840Ek1C9ibGtQGD44w9fTXuK`,
    // };

    const response = await httpService<any>(
      httpConstants.METHOD_TYPE.POST,
      AGENT_URL_2,
      payload,
      undefined,
      true
    );

    return response;
  } catch (error) {
    console.error('Error making AI enablement sync call:', error);
    throw error;
  }
};

const EnablementCard: React.FC<{
  title: string;
  content: string;
  bgcolor: string;
  padding?: string;
}> = ({ title, content, bgcolor, padding = '16px' }) => {
  return (
    <Box
      sx={{
        p: {
          padding,
        },
        borderRadius: '8px',
        bgcolor,
      }}
    >
      <Typography
        sx={{
          fontFamily: 'Poppins',
          fontWeight: 600,
          fontSize: '14px',
          lineHeight: '20px',
          letterSpacing: '-0.01em',
          mb: 1,
          color: '#111827',
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          fontFamily: 'Poppins',
          fontWeight: 400,
          fontSize: '15px',
          lineHeight: '20px',
          letterSpacing: '-0.01em',
          color: '#000000',
        }}
      >
        {content}
      </Typography>
    </Box>
  );
};
const getApprovalStatusConfig = (status?: string | null) => {
  if (!status) return { text: '', show: false };

  const normalized = String(status).trim().toLowerCase();

  switch (normalized) {
    case 'approval_pending':
      return {
        text: 'Approval pending',
        color: '#FF0505',
        bgColor: '#FFF5F2',
        borderColor: '#FF0505',
        show: true,
      };

    case 'approved':
      return {
        text: 'Approved',
        color: '#11953E',
        bgColor: '#DBFFE8',
        borderColor: '#11953E',
        show: true,
      };

    case 'solutioning':
      return {
        text: 'Solutioning',
        color: '#11953E',
        bgColor: '#DBFFE8',
        borderColor: '#11953E',
        show: true,
      };

    case 'ready_to_build':
      return {
        text: 'Ready to Build',
        color: '#626262',
        bgColor: 'rgba(169,169,169,0.16)',
        borderColor: '#626262',
        show: true,
      };

    case 'build':
      return {
        text: 'Build',
        color: '#11953E',
        bgColor: '#F3E8FF',
        borderColor: '#11953E',
        show: true,
      };

    default:
      return { text: '', show: false };
  }
};
export type EditType =
  | 'overview'
  | 'roi'
  | 'complexity'
  | 'key_requirements'
  | 'data_requirements'
  | 'benefits'
  | 'agent_name'
  | 'add_key_requirements'
  | 'add_data_requirements'
  | 'name'
  | 'impact'
  | 'embedded'
  | 'extended'
  | 'custom'
  | 'risk';

type ViewSolutionDetailsProps = {
  selectedSolutionId: AISolutionId;
  selectedSolutionFromLibrary: any;
  enterpriseContextApplied?: boolean;
  setEnterpriseContextApplied?: (value: boolean) => void;
  aiSolutionData?: any;
  setAiSolutionData?: any;
  diagramId?: string;
  // setSelectedSolutionFromLibrary: (value: any) => void;
  /** When returned, the boolean is the new vault state after the API call; used to keep button text in sync with the vault API. */
  handleToggleVault: () => void | Promise<void | boolean>;
  handleUpdateSolution: (data: any) => void;
  handleSave: (data: any) => void;
  isEditable?: boolean;
  isProcessDiagramActive?: boolean;
  onApproval?: (updatedSolution: any) => void;
  handleRemoveClick?: () => void;
  closeDrawer?: () => void;
  isIdeation?: boolean;
  processType?: 'custom' | 'standard' | string;
  fromList?: boolean;
  breadcrumb?: string[];
  processName?: string;
  isProcessAiOpportunities?: boolean;
};

const ViewSolutionDetails: React.FC<ViewSolutionDetailsProps> = ({
  selectedSolutionId,
  selectedSolutionFromLibrary,
  enterpriseContextApplied = false,
  setEnterpriseContextApplied = () => { },
  aiSolutionData,
  setAiSolutionData,
  diagramId,
  // setSelectedSolutionFromLibrary,
  handleToggleVault,
  handleUpdateSolution,
  handleSave,
  isEditable = true,
  isProcessDiagramActive = false,
  onApproval,
  handleRemoveClick,
  closeDrawer,
  isIdeation = false,
  processType = 'standard',
  fromList = false,
  isProcessAiOpportunities = false,
  breadcrumb = [],
  processName = '',
}) => {
  const [, setSolution] = useState<IEnhancedAISolution | null>(null);
  const [, setSource] = useState<string[]>([]);
  const [isFavorite, setIsFavorite] = useState<boolean>(false);
  const [, setDataSources] = useState<string>('');
  const [, setImplementationConsiderations] = useState<string>('');
  const [showApplySuccessSnackbar, setShowApplySuccessSnackbar] = useState(false);
  const [activePiece, setActivePiece] = useState<string>('information');
  const [agentLlmResponse, setAgentLlmResponse] = useState<ExtraAgentData[]>([]);
  const [isLoadingAgentLlmResponse, setIsLoadingAgentLlmResponse] = useState<boolean>(false);

  const currentSolutionIdRef = useRef<AISolutionId | null>(null);
  const hasLoadedImplementationRef = useRef<boolean>(false);
  const lastValidDisplayDataRef = useRef<any>(null);

  const currentOrg = useMemo(() => localStorage.getItem('current_organization') || 'Empty', []);
  const [showFavoriteModal, setShowFavoriteModal] = useState(false);
  const [queryLoading, setQueryLoading] = useState(false);
  const [queryError, setQueryError] = useState<any>(null);
  const [llmResponseLoading, setLlmResponseLoading] = useState<boolean>(false);
  const [llmResponseData, setLlmResponseData] = useState<any>([]);
  const [isLoadingImplementation] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const isMobile = useMediaQuery('(max-width: 600px)');
  const [updatedSolutionData, setUpdatedSolutionData] = useState<any>(null);
  const [editItem, setEditItem] = useState<any>(null);
  const [originalSolutionData, setOriginalSolutionData] = useState<any>(null);
  const [openEditModal, setOpenEditModal] = useState<boolean>(false);
  const [editType, setEditType] = useState<EditType | string>('');
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState<boolean>(false);
  const [updatedAiSolution, setUpdatedAiSolution] = useState<any>(null);
  const [hasEnterpriseTechApplied, setHasEnterpriseTechApplied] = useState<boolean>(false);

  const [enablementExpanded, setEnablementExpanded] = useState<Record<string, boolean>>({
    embedded: true,
    extended: true,
    custom: true,
  });

  const navigate = useNavigate();

  const [agentClickCount, setAgentClickCount] = useState<number>(0);
  const [lastClickTime, setLastClickTime] = useState<number>(0);
  const [isManualSyncLoading, setIsManualSyncLoading] = useState<boolean>(false);
  const [, setIsLoadingEnterpriseTech] = useState<boolean>(false);
  const hasFetchedEnterpriseTechRef = useRef<boolean>(false);

  // Enhanced cache structure to track recently synced solutions - persists across component instances
  type SolutionSyncCacheEntry = {
    timestamp: number;
    wasManualSync: boolean;
    hasAgentData: boolean; // tracks if both data sources and enablement exist
  };

  if (!(window as any).solutionSyncCache) {
    (window as any).solutionSyncCache = new Map<string, SolutionSyncCacheEntry>();
  }
  const globalSyncedSolutions = (window as any).solutionSyncCache as Map<
    string,
    SolutionSyncCacheEntry
  >;

  // Global cache to persist agent data across component remounts
  if (!(window as any).agentDataCache) {
    (window as any).agentDataCache = new Map<string, any>();
  }
  const globalAgentDataCache = (window as any).agentDataCache as Map<string, any>;

  // Global state for enterprise technology updates
  if (!(window as any).enterpriseTechLastUpdated) {
    (window as any).enterpriseTechLastUpdated = Date.now();
  }

  // Cache timeout: 30 minutes (in milliseconds)
  const CACHE_TIMEOUT = 30 * 60 * 1000;

  // Approval status state
  const [approvalStatus, setApprovalStatus] = useState<string | null>(null);
  const [isVaultActionLoading, setIsVaultActionLoading] = useState<boolean>(false);
  const [localVaultState, setLocalVaultState] = useState<boolean>(
    updatedAiSolution?.vault ?? aiSolutionData?.vault ?? false
  );

  const vaultStateManuallyChangedRef = useRef<boolean>(false);

  const recentlySyncedRef = useRef<Set<string>>(new Set());

  // Reset sync state when solution changes
  useEffect(() => {
    if (currentSolutionIdRef.current !== selectedSolutionId) {
      currentSolutionIdRef.current = selectedSolutionId;
      hasLoadedImplementationRef.current = false;
      hasFetchedEnterpriseTechRef.current = false;
      setDataSources('');
      setImplementationConsiderations('');
      setSource([]);

      vaultStateManuallyChangedRef.current = false;

      // Reset admin sync state
      setAgentClickCount(0);
      setLastClickTime(0);
      setIsManualSyncLoading(false);
      setActivePiece('information');

      // Reset edit state and data when solution changes
      setUpdatedSolutionData(null);
      setOriginalSolutionData(null);
      setIsEditing(false);

      // Don't clear cache when switching solutions - let it persist across navigation

      // Reset edit state and data when solution changes
      setUpdatedSolutionData(null);
      setOriginalSolutionData(null);
      setIsEditing(false);
    }
  }, [selectedSolutionId]);

  const isIdeationEnablementLoading = isManualSyncLoading || isLoadingAgentLlmResponse;
  console.log('loading', isIdeationEnablementLoading);

  // Add IMPLEMENTATION_STYLES for Agents view
  const IMPLEMENTATION_STYLES: Record<string, { bg: string; color: string; icon: string }> = {
    'Client Specific': {
      bg: '#F1FFEE',
      color: '#27742A',
      icon: '/images/client_specific.svg',
    },
    Embedded: {
      bg: '#FFF7F3',
      color: '#9A5A37',
      icon: '/images/embedded_icon.svg',
    },
    Extended: {
      bg: '#FFF7DF',
      color: '#B18600',
      icon: '/images/extended_icon.svg',
    },
  };

  const renderInfoBox = ({
    title,
    titleColor,
    icon,
    text,
    bg,
    extraSx = {},
  }: {
    title: string;
    titleColor: string;
    icon: string;
    text: string;
    bg: string;
    extraSx?: object;
  }) => {
    return (
      <Box
        sx={{
          background: bg,
          borderRadius: '6px',
          padding: '12px',
          mt: '18px',
          width: { xs: '100%', sm: '50%' },
          minHeight: '126px',
          ...extraSx,
        }}
      >
        <Box sx={{ display: 'flex' }}>
          <img src={icon} alt="icon" />
          <Typography sx={{ fontWeight: 500, fontSize: '15px', color: titleColor, ml: '8px' }}>
            {title}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: '15px', fontWeight: 400, mt: '10px' }}>{text}</Typography>
      </Box>
    );
  };

  // Merge agent sync responses without wiping previously mapped fields
  const mergeAgentSyncData = useCallback(
    (baseData: any, syncResponse: any) => {
      const source = baseData || aiSolutionData || {};

      // Helper to get agent field with multiple fallback sources
      const getAgentField = (
        fieldName: 'embedded' | 'extended' | 'custom',
        syncResponse: any,
        source: any
      ) => {
        // Priority 1: syncResponse.ai_enablement.enablement_matrix.* (preferred)
        const fromEnablementMatrix = syncResponse?.ai_enablement?.enablement_matrix?.[fieldName];
        if (Array.isArray(fromEnablementMatrix) && fromEnablementMatrix.length > 0) {
          return fromEnablementMatrix;
        }

        // Priority 2: syncResponse.embedded/extended/custom (direct fields)
        const fromSyncDirect = syncResponse?.[fieldName];
        if (Array.isArray(fromSyncDirect) && fromSyncDirect.length > 0) {
          return fromSyncDirect;
        }

        // Special case for embedded: also check agent_classification
        if (
          fieldName === 'embedded' &&
          Array.isArray(syncResponse?.agent_classification) &&
          syncResponse.agent_classification.length > 0
        ) {
          return syncResponse.agent_classification;
        }

        // Priority 3: source.agent.embedded/extended/custom (preserve existing)
        const fromSource = source?.agent?.[fieldName];
        if (Array.isArray(fromSource) && fromSource.length > 0) {
          return fromSource;
        }

        // Priority 4: aiSolutionData.agent.embedded/extended/custom (fallback to original)
        const fromOriginal = aiSolutionData?.agent?.[fieldName];
        if (Array.isArray(fromOriginal) && fromOriginal.length > 0) {
          return fromOriginal;
        }

        // Priority 5: Only use [] as last resort
        return [];
      };

      // Preserve enablement_matrix structure if it exists in source but not in syncResponse
      const preservedEnablementMatrix = source?.agent?.ai_enablement?.enablement_matrix;
      const syncEnablementMatrix = syncResponse?.ai_enablement?.enablement_matrix;

      // Merge enablement_matrix if both exist, preserving existing structure
      let mergedEnablementMatrix = syncEnablementMatrix || preservedEnablementMatrix;
      if (preservedEnablementMatrix && syncEnablementMatrix) {
        mergedEnablementMatrix = {
          embedded:
            Array.isArray(syncEnablementMatrix.embedded) && syncEnablementMatrix.embedded.length > 0
              ? syncEnablementMatrix.embedded
              : preservedEnablementMatrix.embedded || [],
          extended:
            Array.isArray(syncEnablementMatrix.extended) && syncEnablementMatrix.extended.length > 0
              ? syncEnablementMatrix.extended
              : preservedEnablementMatrix.extended || [],
          custom:
            Array.isArray(syncEnablementMatrix.custom) && syncEnablementMatrix.custom.length > 0
              ? syncEnablementMatrix.custom
              : preservedEnablementMatrix.custom || [],
        };
      }

      // Build merged ai_enablement, preserving structure
      const mergedAiEnablement = syncResponse?.ai_enablement
        ? {
          ...syncResponse.ai_enablement,
          enablement_matrix:
            mergedEnablementMatrix || syncResponse.ai_enablement.enablement_matrix,
        }
        : source?.agent?.ai_enablement || aiSolutionData?.agent?.ai_enablement;

      return {
        ...source,
        data_sources: source?.data_sources ?? aiSolutionData?.data_sources ?? {},
        key_requirements: source?.key_requirements ?? aiSolutionData?.key_requirements ?? [],
        benefits: source?.benefits ?? aiSolutionData?.benefits ?? {},
        agent: {
          ...source?.agent,
          ai_enablement: mergedAiEnablement,
          ai_solution:
            syncResponse?.application_in_use ||
            syncResponse?.ai_solution ||
            source?.agent?.ai_solution ||
            aiSolutionData?.agent?.ai_solution ||
            [],
          agent_classification:
            syncResponse?.agent_classification ||
            source?.agent?.agent_classification ||
            aiSolutionData?.agent?.agent_classification ||
            [],
          embedded: getAgentField('embedded', syncResponse, source),
          extended: getAgentField('extended', syncResponse, source),
          custom: getAgentField('custom', syncResponse, source),
        },
      };
    },
    [aiSolutionData]
  );

  // Add this function to handle approval
  const handleSendForApproval = async () => {
    try {
      // API call to send for approval
      const url = `${API_BASE}/api/v1/e2e_taxonomy/new_ai_opportunity/${selectedSolutionId}/`;
      await httpService(httpConstants.METHOD_TYPE.PATCH, url, {
        status: 'approval_pending',
        isAddOpportunityStatusHistory: true,
        organization_id: currentOrg,
      });

      setApprovalStatus('pending');
      // Refetch solution data to get the actual approval status from API
      const updated = await fetchAISolutionWithAgentEnablement({
        solutionId: String(selectedSolutionId),
        diagramId: diagramId || '',
        organizationId: currentOrg,
      });

      // Notify parent about updated solution so it can update its list / UI
      if (onApproval) {
        try {
          onApproval(updated);
        } catch (e) {
          console.warn('onApproval callback failed', e);
        }
      }

      // ShowToast({
      //   message: 'Solution sent for approval successfully',
      //   type: ToastTypeEnum.Success
      // });
    } catch (error) {
      console.error('Error sending for approval:', error);
      // ShowToast({
      //   message: 'Error sending solution for approval',
      //   type: ToastTypeEnum.Error
      // });
    }
  };

  const { mutate } = useGraphQLMutation<IMutationAISolution>(UPDATE_FAVORITE_AI_SOLUTIONS, {
    onCompleted: (data) => {
      console.log('Favorite updated successfully:', data);
    },
    onError: (error) => {
      console.error('Error updating favorite:', error);
    },
    refetchQueries: ['IMutationAISolution'],
  });

  // Helper to normalize/prepare solution data for UI
  const processSolutionData = useCallback((rawSolution: IAISolution): IEnhancedAISolution => {
    const allDataRequirements =
      rawSolution.aiOpportunitiesUsed?.flatMap((item) => item.data_requirements || []).join(', ') ||
      '';
    const allTopBenefits = rawSolution?.benefits
      ? Object.entries(rawSolution.benefits).map(([key, value]) => ({
        title: getBenefitTitle(key),
        label: getBenefitLabelNew(value as string),
      }))
      : [];
    const keyConsiderations = rawSolution?.keyConsiderations
      ? rawSolution?.keyConsiderations
      : rawSolution.potentialChallenges
        ?.split(',')
        .map((i) => i.trim())
        .filter((i) => i.length > 0) || [];
    const allAgents: IAllAgents | undefined = rawSolution.agentCollaboration?.length
      ? {
        description: rawSolution.agentCollaboration.map((c) => c.collaboration).join(' '),
        agents: rawSolution.agentCollaboration
          .flatMap((c) => c.agents)
          .map((agent) => {
            const match = rawSolution.aiOpportunitiesUsed?.find(
              (o) => o.ai_opportunity_id === agent.agent_id
            );
            const isMandatory = match?.must_have === 'MUST HAVE';
            return {
              label: agent.agent_name,
              mustHave: match?.must_have,
              mustHaveLabel: isMandatory ? 'Essential' : 'Optional',
              className: isMandatory ? 'yellow' : 'blue',
            };
          }),
      }
      : undefined;
    const impact = rawSolution.impact;
    return {
      ...rawSolution,
      allDataRequirements,
      allTopBenefits,
      keyConsiderations,
      allAgents,
      impact,
    };
  }, []);

  useEffect(() => {
    if (!vaultStateManuallyChangedRef.current) {
      const vaultState = selectedSolutionFromLibrary?.vault ? selectedSolutionFromLibrary?.vault : aiSolutionData?.vault ? aiSolutionData?.vault : false;
      setLocalVaultState(vaultState);
    }
  }, [aiSolutionData?.vault, selectedSolutionFromLibrary?.vault]);
  const handleVaultClick = useCallback(async () => {
    if (isVaultActionLoading) return;
    setIsVaultActionLoading(true);
    vaultStateManuallyChangedRef.current = true;
    try {
      setLocalVaultState((prevState) => !prevState);

      const result = handleToggleVault?.();
      const resolved = await Promise.resolve(result);

      if (typeof resolved === 'boolean') {
        setLocalVaultState(resolved);
        // Delay clearing ref so the sync effect does not run with stale props and overwrite this value
        setTimeout(() => {
          vaultStateManuallyChangedRef.current = false;
        }, 400);
      } else {
        setTimeout(() => {
          vaultStateManuallyChangedRef.current = false;
        }, 500);
      }
    } catch (err) {
      console.error('Vault action failed', err);
      const originalState = updatedAiSolution?.vault ?? aiSolutionData?.vault ?? selectedSolutionFromLibrary?.vault ?? false;
      setLocalVaultState(originalState);
      vaultStateManuallyChangedRef.current = false;
    } finally {
      setTimeout(() => setIsVaultActionLoading(false), 300);
    }
  }, [
    handleToggleVault,
    isVaultActionLoading,
    updatedAiSolution?.vault,
    aiSolutionData?.vault,
    selectedSolutionFromLibrary?.vault,
  ]);

  const fetchSolutionById = useCallback(async (): Promise<void> => {
    if (!selectedSolutionId) {
      return;
    }

    if (String(selectedSolutionId).startsWith('mock-')) {
      setQueryLoading(false);
      return;
    }

    setQueryLoading(true);
    setQueryError(null);
    setLlmResponseData([]);

    try {
      // Only call fetchAISolutionWithAgentEnablement for ideation or custom processes
      // For default processes, use agent key and don't call this API
      // Note: effectiveIsIdeation will be determined later based on displayData, so use prop here
      const shouldCallAgentEnablementAPI =
        (isIdeation || processType === 'custom') && !isProcessAiOpportunities;

      let response;
      if (shouldCallAgentEnablementAPI) {
        response = await fetchAISolutionWithAgentEnablement({
          solutionId: String(selectedSolutionId),
          diagramId: diagramId || '',
          organizationId: currentOrg,
        });
      } else {
        // For default processes, use fetchAISolutionByID instead
        response = await fetchAISolutionByID({
          solutionId: String(selectedSolutionId),
          diagramId: diagramId || '',
          organizationId: currentOrg,
        });
      }

      if (!response) {
        throw new Error('Invalid response structure');
      }

      setAiSolutionData?.(response);
    } catch (err: unknown) {
      setQueryError(err);
      throw err;
    } finally {
      setQueryLoading(false);
    }
  }, [selectedSolutionId, diagramId, currentOrg, setAiSolutionData, isIdeation, processType]);

  useEffect(() => {
    if (aiSolutionData && Object.keys(aiSolutionData).includes('agent')) {
      setUpdatedAiSolution(aiSolutionData);
    }
  }, [aiSolutionData]);

  useEffect(() => {
    if (selectedSolutionId) {
      fetchSolutionById();
    }
  }, [selectedSolutionId, fetchSolutionById]);

  // Initialize original data when solution is first fetched (not during edits)
  useEffect(() => {
    if (!queryLoading && !isEditing && aiSolutionData && selectedSolutionId) {
      // Only update originalSolutionData on fresh loads
      if (String(aiSolutionData.id) === String(selectedSolutionId)) {
        setOriginalSolutionData(JSON.parse(JSON.stringify(aiSolutionData)));
      }
    }
  }, [selectedSolutionId, queryLoading, isEditing]);

  useEffect(() => {
    if (queryLoading) return;
    if (queryError) return;
    if (aiSolutionData) {
      const rawSolution = aiSolutionData;
      const enhancedSolution = processSolutionData(rawSolution);
      setSolution(enhancedSolution);
      setIsFavorite(rawSolution.favorite || false);

      // Initialize agent data from API response if available
      if (aiSolutionData.agent) {
        const agentData = aiSolutionData.agent;

        // Set agent response data for display
        if (
          agentData.ai_enablement ||
          agentData.agent_classification ||
          agentData.embedded ||
          agentData.extended ||
          agentData.custom
        ) {
          const agentResponse =
            agentData.agent_classification ||
            agentData.embedded ||
            agentData.extended ||
            agentData.custom ||
            agentData.ai_enablement ||
            [];
          setAgentLlmResponse(agentResponse);

          const llmData = agentData.ai_solution || agentData.application_in_use || [];
          setLlmResponseData(llmData);
        }
      }

      setApprovalStatus(rawSolution.status || null);
    }
    setUpdatedSolutionData(aiSolutionData);
  }, [aiSolutionData, updatedAiSolution, queryLoading, queryError, processSolutionData]);

  console.log('sss', updatedAiSolution, aiSolutionData);

  useEffect(() => {
    if (!aiSolutionData?.id || !currentOrg || !selectedSolutionId) return;

    // CRITICAL FIX: Ensure aiSolutionData.id matches selectedSolutionId to prevent cache mismatch
    // This prevents using cached data from a different solution when switching quickly
    if (String(aiSolutionData.id) !== String(selectedSolutionId)) {
      console.log(
        `⚠️ Solution ID mismatch: aiSolutionData.id (${aiSolutionData.id}) !== selectedSolutionId (${selectedSolutionId}). Waiting for sync...`
      );
      return;
    }

    // Use selectedSolutionId as the source of truth for cache key to prevent race conditions
    // Convert to string for cache key consistency
    const solutionId = String(selectedSolutionId);

    const fetchEnterpriseTechnology = async () => {
      if (!aiSolutionData || !aiSolutionData.id) {
        return;
      }

      // Double-check that IDs still match (in case aiSolutionData changed during async execution)
      if (String(aiSolutionData.id) !== String(selectedSolutionId)) {
        console.log(
          `⚠️ Solution ID mismatch during fetch: aiSolutionData.id (${aiSolutionData.id}) !== selectedSolutionId (${selectedSolutionId}). Aborting.`
        );
        hasFetchedEnterpriseTechRef.current = false; // Reset ref so it can retry
        return;
      }

      /** -------------------------------
     * RENDER CYCLE GUARD
     -------------------------------- */
      if (hasFetchedEnterpriseTechRef.current) {
        return;
      }
      hasFetchedEnterpriseTechRef.current = true;

      // Check if solution was recently synced (not manual) and has cached data
      const cachedSyncEntry = globalSyncedSolutions.get(solutionId);
      const cachedAgentData = globalAgentDataCache.get(solutionId);

      if (cachedSyncEntry && !cachedSyncEntry.wasManualSync) {
        const timeSinceSync = Date.now() - cachedSyncEntry.timestamp;
        const isRecent = timeSinceSync < CACHE_TIMEOUT;

        if (isRecent && cachedSyncEntry.hasAgentData && cachedAgentData) {
          // CRITICAL: Verify cached data belongs to the current solution
          const cachedSolutionId = cachedAgentData.solutionData?.id;
          if (cachedSolutionId && String(cachedSolutionId) !== String(solutionId)) {
            console.warn(
              `⚠️ Cached data ID mismatch: cached (${cachedSolutionId}) !== current (${solutionId}). Skipping cache.`
            );
            hasFetchedEnterpriseTechRef.current = false; // Reset to allow fresh fetch
            // Fall through to sync logic below
          } else {
            // Validate that cached data contains complete agent structure
            const validateAgentDataCompleteness = (solutionData: any): boolean => {
              if (!solutionData?.agent) return false;

              const agent = solutionData.agent;

              // Check for enablement_matrix structure
              const hasEnablementMatrix =
                agent?.ai_enablement?.enablement_matrix &&
                typeof agent.ai_enablement.enablement_matrix === 'object' &&
                (Array.isArray(agent.ai_enablement.enablement_matrix.embedded) ||
                  Array.isArray(agent.ai_enablement.enablement_matrix.extended) ||
                  Array.isArray(agent.ai_enablement.enablement_matrix.custom));

              // Check for direct embedded/extended/custom arrays
              const hasDirectArrays =
                (Array.isArray(agent.embedded) && agent.embedded.length > 0) ||
                (Array.isArray(agent.extended) && agent.extended.length > 0) ||
                (Array.isArray(agent.custom) && agent.custom.length > 0);

              // Check for agent.embedded array with implementation_type classification
              const hasEmbeddedWithImplementationType =
                Array.isArray(agent.embedded) &&
                agent.embedded.length > 0 &&
                agent.embedded.some((item: any) => item?.implementation_type);

              return hasEnablementMatrix || hasDirectArrays || hasEmbeddedWithImplementationType;
            };

            const cachedSolutionData = cachedAgentData.solutionData;
            const isCachedDataComplete = validateAgentDataCompleteness(cachedSolutionData);

            if (!isCachedDataComplete) {
              console.warn(
                `⚠️ Cached data incomplete: Missing complete agent structure. Skipping cache restoration.`
              );
              hasFetchedEnterpriseTechRef.current = false; // Reset to allow fresh fetch
              // Fall through to sync logic below
            } else {
              console.log('✅ Using cached agent data - skipping sync calls');

              // Still need to fetch enterprise tech to verify it's still configured
              // But we'll use cached data if enterprise tech check passes
              try {
                const enterpriseTechResponse = await getEnterpriseTechnology(currentOrg);
                const hasEnterpriseTech =
                  enterpriseTechResponse &&
                  enterpriseTechResponse.industry_data &&
                  Object.keys(enterpriseTechResponse.industry_data).length > 0;

                // Restore cached agent data immediately
                if (cachedAgentData.agentData) {
                  setAgentLlmResponse(cachedAgentData.agentData.agentLlmResponse || []);
                  setLlmResponseData(cachedAgentData.agentData.llmResponseData || []);
                }

                if (cachedAgentData.solutionData) {
                  setUpdatedSolutionData(cachedAgentData.solutionData);
                  setAiSolutionData?.(cachedAgentData.solutionData);

                  // Since cachedSyncEntry.hasAgentData is true, we know enablement exists
                  // But also verify the actual structure has enablement data
                  const solutionData = cachedAgentData.solutionData;
                  const agent = solutionData?.agent;

                  // Check for enablement data in different possible structures:
                  // 1. For ideation: agent.ai_enablement.enablement_matrix
                  // 2. For process-xplr: agent.embedded, agent.extended, agent.custom
                  // 3. General: agent.ai_enablement, agent.agent_classification
                  const hasCachedEnablement =
                    // Ideation enablement structure
                    (agent?.ai_enablement?.enablement_matrix &&
                      (agent.ai_enablement.enablement_matrix.embedded?.length > 0 ||
                        agent.ai_enablement.enablement_matrix.extended?.length > 0 ||
                        agent.ai_enablement.enablement_matrix.custom?.length > 0)) ||
                    // Process-xplr enablement structure
                    (agent?.embedded &&
                      Array.isArray(agent.embedded) &&
                      agent.embedded.length > 0) ||
                    (agent?.extended &&
                      Array.isArray(agent.extended) &&
                      agent.extended.length > 0) ||
                    (agent?.custom && Array.isArray(agent.custom) && agent.custom.length > 0) ||
                    // General enablement structures
                    (agent?.agent_classification &&
                      Array.isArray(agent.agent_classification) &&
                      agent.agent_classification.length > 0) ||
                    (agent?.ai_enablement && typeof agent.ai_enablement === 'object');

                  // Set hasEnterpriseTechApplied based on both enterprise tech availability AND cached enablement
                  // This ensures the UI shows enablement instead of "Enablement Not Available"
                  if (hasEnterpriseTech && hasCachedEnablement) {
                    setHasEnterpriseTechApplied(true);
                  } else {
                    // If enterprise tech is not configured anymore, set to false
                    setHasEnterpriseTechApplied(false);
                  }
                }

                setIsLoadingEnterpriseTech(false);
                setLlmResponseLoading(false);
                setIsLoadingAgentLlmResponse(false);
                return;
              } catch (error) {
                console.error('Error fetching enterprise tech for cache validation:', error);
                // On error, still restore cached data but verify enablement structure
                if (cachedAgentData.agentData) {
                  setAgentLlmResponse(cachedAgentData.agentData.agentLlmResponse || []);
                  setLlmResponseData(cachedAgentData.agentData.llmResponseData || []);
                }

                if (cachedAgentData.solutionData) {
                  setUpdatedSolutionData(cachedAgentData.solutionData);
                  setAiSolutionData?.(cachedAgentData.solutionData);

                  const solutionData = cachedAgentData.solutionData;
                  const agent = solutionData?.agent;
                  const hasCachedEnablement =
                    (agent?.ai_enablement?.enablement_matrix &&
                      (agent.ai_enablement.enablement_matrix.embedded?.length > 0 ||
                        agent.ai_enablement.enablement_matrix.extended?.length > 0 ||
                        agent.ai_enablement.enablement_matrix.custom?.length > 0)) ||
                    (agent?.embedded &&
                      Array.isArray(agent.embedded) &&
                      agent.embedded.length > 0) ||
                    (agent?.extended &&
                      Array.isArray(agent.extended) &&
                      agent.extended.length > 0) ||
                    (agent?.custom && Array.isArray(agent.custom) && agent.custom.length > 0) ||
                    (agent?.agent_classification &&
                      Array.isArray(agent.agent_classification) &&
                      agent.agent_classification.length > 0) ||
                    (agent?.ai_enablement && typeof agent.ai_enablement === 'object');

                  // If cached data has enablement, set to true (assume enterprise tech is still available)
                  if (hasCachedEnablement) {
                    setHasEnterpriseTechApplied(true);
                  }
                }

                setIsLoadingEnterpriseTech(false);
                setLlmResponseLoading(false);
                setIsLoadingAgentLlmResponse(false);
                return;
              }
            }
          }
        } else if (isRecent && !cachedSyncEntry.hasAgentData) {
          console.log('🔄 Cache exists but incomplete - will proceed with sync');
        } else {
          console.log('🔄 Cache expired - will proceed with sync');
        }
      }

      const markSynced = (wasManual = false, hasCompleteData = false) => {
        recentlySyncedRef.current.add(solutionId);
        globalSyncedSolutions.set(solutionId, {
          timestamp: Date.now(),
          wasManualSync: wasManual,
          hasAgentData: hasCompleteData,
        });
      };

      try {
        setIsLoadingEnterpriseTech(true);
        setLlmResponseLoading(true);

        // Always fetch fresh enterprise technology data to detect updates when landscape changes
        console.log('Fetching fresh enterprise technology data for sync decision');

        // Always fetch enterprise technology first to check if it's available
        const enterpriseTechResponse = await getEnterpriseTechnology(currentOrg);

        // Check if organization has enterprise technology configured
        // If enterprise technology exists, we should try to get agent data

        const hasEnterpriseTech =
          enterpriseTechResponse &&
          enterpriseTechResponse.industry_data &&
          Object.keys(enterpriseTechResponse.industry_data).length > 0;

        setHasEnterpriseTechApplied(hasEnterpriseTech);

        if (!hasEnterpriseTech) {
          console.log('No enterprise technology configured');
          return;
        }

        // If enterprise technology exists, always try to get agent data
        // First check if agent data already exists and is usable
        const hasExistingAgentData =
          aiSolutionData.agent &&
          (aiSolutionData.agent.ai_enablement ||
            aiSolutionData.agent.agent_classification ||
            aiSolutionData.agent.embedded ||
            aiSolutionData.agent.extended ||
            aiSolutionData.agent.custom);

        if (hasExistingAgentData) {
          // Agent data exists - check if it needs updating based on timestamps
          if (aiSolutionData.agent.updated_at) {
            const shouldSync = await checkIfSyncNeeded(
              enterpriseTechResponse,
              aiSolutionData.agent
            );

            if (shouldSync) {
              setIsLoadingAgentLlmResponse(true);

              try {
                // Step 1: Call data sources sync (AGENT_URL_1)
                const dataSourcesResponse = await makeDataSourcesSyncCall(
                  aiSolutionData,
                  enterpriseTechResponse,
                  {},
                  processType
                );

                // Step 2: Update state immediately after first call completes
                const firstResponseData =
                  dataSourcesResponse?.application_in_use || dataSourcesResponse?.ai_solution || [];

                // Update both states to ensure data is available
                setLlmResponseData(firstResponseData);

                setUpdatedSolutionData((prev: any) => {
                  const base = prev || aiSolutionData || {};
                  // CRITICAL: Preserve ALL existing agent fields (embedded, extended, custom, enablement_matrix)
                  // to prevent sections from disappearing during intermediate update
                  return {
                    ...base,
                    agent: {
                      ...base.agent,
                      // Preserve existing agent structure
                      embedded: base.agent?.embedded || aiSolutionData?.agent?.embedded || [],
                      extended: base.agent?.extended || aiSolutionData?.agent?.extended || [],
                      custom: base.agent?.custom || aiSolutionData?.agent?.custom || [],
                      ai_enablement:
                        base.agent?.ai_enablement || aiSolutionData?.agent?.ai_enablement,
                      agent_classification:
                        base.agent?.agent_classification ||
                        aiSolutionData?.agent?.agent_classification ||
                        [],
                      // Update only ai_solution
                      ai_solution: firstResponseData,
                    },
                  };
                });

                // Hide skeleton for data sources now that first call is complete
                // React will batch these state updates together
                setLlmResponseLoading(false);

                // Step 3: Call AI enablement sync (AGENT_URL_2)
                const enablementResponse = await makeAiEnablementSyncCall(
                  aiSolutionData,
                  enterpriseTechResponse,
                  {},
                  processType
                );

                // Step 4: Merge both responses and update final state
                const combinedResponse = mergeSyncResponses(
                  dataSourcesResponse,
                  enablementResponse
                );

                const agentResponseData =
                  combinedResponse?.agent_classification ||
                  combinedResponse?.ai_enablement?.enablement_matrix?.embedded ||
                  combinedResponse?.embedded ||
                  [];
                setAgentLlmResponse(agentResponseData);

                // Update with combined response (may include additional data from second call)
                setLlmResponseData(
                  combinedResponse?.application_in_use || combinedResponse?.ai_solution || []
                );

                // Update the full agent data structure
                setUpdatedSolutionData((prev: any) => {
                  const merged = mergeAgentSyncData(prev || aiSolutionData, combinedResponse);
                  setAiSolutionData?.(merged);

                  // Cache the merged data for future use
                  const hasCompleteAgentData =
                    merged?.agent?.ai_enablement &&
                    (merged?.agent?.ai_solution || merged?.agent?.application_in_use);
                  if (hasCompleteAgentData) {
                    globalAgentDataCache.set(solutionId, {
                      solutionData: merged,
                      agentData: {
                        agentLlmResponse: agentResponseData,
                        llmResponseData:
                          combinedResponse?.application_in_use ||
                          combinedResponse?.ai_solution ||
                          [],
                      },
                    });
                    markSynced(false, true);
                  }

                  return merged;
                });
              } catch (error) {
                console.error('Sync failed:', error);
                // Fall back to existing data from fetchAISolutionByID
                setAgentLlmResponse(
                  aiSolutionData.agent.agent_classification ||
                  aiSolutionData.agent.embedded ||
                  aiSolutionData.agent.extended ||
                  aiSolutionData.agent.custom ||
                  aiSolutionData.agent.ai_enablement ||
                  []
                );
                setLlmResponseData(
                  aiSolutionData.agent?.ai_solution ||
                  aiSolutionData.agent?.application_in_use ||
                  []
                );
                // Hide skeleton on error
                setLlmResponseLoading(false);
              } finally {
                setIsLoadingAgentLlmResponse(false);
              }
            } else {
              console.log('Agent is up to date, no sync needed');
              // Set data from existing agent data from fetchAISolutionByID
              setAgentLlmResponse(
                aiSolutionData.agent.agent_classification ||
                aiSolutionData.agent.embedded ||
                aiSolutionData.agent.extended ||
                aiSolutionData.agent.custom ||
                aiSolutionData.agent.ai_enablement ||
                []
              );
              setLlmResponseData(
                aiSolutionData.agent?.ai_solution || aiSolutionData.agent?.application_in_use || []
              );
              setIsLoadingAgentLlmResponse(false);

              // Cache existing agent data
              const hasCompleteAgentData =
                aiSolutionData.agent?.ai_enablement &&
                (aiSolutionData.agent?.ai_solution || aiSolutionData.agent?.application_in_use);
              if (hasCompleteAgentData) {
                globalAgentDataCache.set(solutionId, {
                  solutionData: aiSolutionData,
                  agentData: {
                    agentLlmResponse:
                      aiSolutionData.agent.agent_classification ||
                      aiSolutionData.agent.embedded ||
                      aiSolutionData.agent.extended ||
                      aiSolutionData.agent.custom ||
                      aiSolutionData.agent.ai_enablement ||
                      [],
                    llmResponseData:
                      aiSolutionData.agent?.ai_solution ||
                      aiSolutionData.agent?.application_in_use ||
                      [],
                  },
                });
                markSynced(false, true);
              }
            }
          } else {
            console.log('Agent has no timestamp, using existing data');
            // Set data from existing agent data from fetchAISolutionByID when no timestamp
            setAgentLlmResponse(
              aiSolutionData.agent.agent_classification ||
              aiSolutionData.agent.embedded ||
              aiSolutionData.agent.extended ||
              aiSolutionData.agent.custom ||
              aiSolutionData.agent.ai_enablement ||
              []
            );
            setLlmResponseData(
              aiSolutionData.agent.ai_solution || aiSolutionData.agent.application_in_use || []
            );

            // Cache existing agent data
            const hasCompleteAgentData =
              aiSolutionData.agent?.ai_enablement &&
              (aiSolutionData.agent?.ai_solution || aiSolutionData.agent?.application_in_use);
            if (hasCompleteAgentData) {
              globalAgentDataCache.set(solutionId, {
                solutionData: aiSolutionData,
                agentData: {
                  agentLlmResponse:
                    aiSolutionData.agent.agent_classification ||
                    aiSolutionData.agent.embedded ||
                    aiSolutionData.agent.extended ||
                    aiSolutionData.agent.custom ||
                    aiSolutionData.agent.ai_enablement ||
                    [],
                  llmResponseData:
                    aiSolutionData.agent.ai_solution ||
                    aiSolutionData.agent.application_in_use ||
                    [],
                },
              });
              markSynced(false, true);
            }
            return;
          }
        } else {
          // No agent data available - call sync API to generate it
          console.log('No agent data available, calling sync API to generate it');
          setIsLoadingAgentLlmResponse(true);

          try {
            const additionalPayload = {
              llm_agent: aiSolutionData.llm_agent,
              solution_enabler: aiSolutionData.solution_enabler,
              diagram_id: diagramId,
              organization_id: currentOrg,
              type: ENVIRONMENT_TYPE,
            };

            // Step 1: Call data sources sync (AGENT_URL_1)
            const dataSourcesResponse = await makeDataSourcesSyncCall(
              aiSolutionData,
              enterpriseTechResponse,
              additionalPayload,
              processType
            );

            // Step 2: Update state immediately after first call completes
            const firstResponseData =
              dataSourcesResponse?.application_in_use || dataSourcesResponse?.ai_solution || [];

            // Update both states to ensure data is available
            setLlmResponseData(firstResponseData);

            setUpdatedSolutionData((prev: any) => {
              const base = prev || aiSolutionData || {};
              // CRITICAL: Preserve ALL existing agent fields (embedded, extended, custom, enablement_matrix)
              // to prevent sections from disappearing during intermediate update
              return {
                ...base,
                agent: {
                  ...base.agent,
                  // Preserve existing agent structure
                  embedded: base.agent?.embedded || aiSolutionData?.agent?.embedded || [],
                  extended: base.agent?.extended || aiSolutionData?.agent?.extended || [],
                  custom: base.agent?.custom || aiSolutionData?.agent?.custom || [],
                  ai_enablement: base.agent?.ai_enablement || aiSolutionData?.agent?.ai_enablement,
                  agent_classification:
                    base.agent?.agent_classification ||
                    aiSolutionData?.agent?.agent_classification ||
                    [],
                  // Update only ai_solution
                  ai_solution: firstResponseData,
                },
              };
            });

            // Hide skeleton for data sources now that first call is complete
            // React will batch these state updates together
            setLlmResponseLoading(false);

            // Step 3: Call AI enablement sync (AGENT_URL_2)
            const enablementResponse = await makeAiEnablementSyncCall(
              aiSolutionData,
              enterpriseTechResponse,
              additionalPayload,
              processType
            );

            // Step 4: Merge both responses and update final state
            const combinedResponse = mergeSyncResponses(dataSourcesResponse, enablementResponse);

            // For agent response, prioritize agent_classification, then fall back to embedded from enablement_matrix
            const agentResponseData =
              combinedResponse?.agent_classification ||
              combinedResponse?.ai_enablement?.enablement_matrix?.embedded ||
              combinedResponse?.embedded ||
              [];
            setAgentLlmResponse(agentResponseData);

            // Update with combined response (may include additional data from second call)
            setLlmResponseData(
              combinedResponse?.application_in_use || combinedResponse?.ai_solution || []
            );

            setUpdatedSolutionData((prev: any) => {
              const merged = mergeAgentSyncData(prev || aiSolutionData, combinedResponse);
              setAiSolutionData?.(merged);

              // Cache the merged data for future use
              const hasCompleteAgentData =
                merged?.agent?.ai_enablement &&
                (merged?.agent?.ai_solution || merged?.agent?.application_in_use);
              if (hasCompleteAgentData) {
                globalAgentDataCache.set(solutionId, {
                  solutionData: merged,
                  agentData: {
                    agentLlmResponse: agentResponseData,
                    llmResponseData:
                      combinedResponse?.application_in_use || combinedResponse?.ai_solution || [],
                  },
                });
                markSynced(false, true);
              }

              return merged;
            });
          } catch (error) {
            console.error('Failed to generate agent data:', error);
            // Hide skeleton on error
            setLlmResponseLoading(false);
            markSynced(false, false);
          } finally {
            setIsLoadingAgentLlmResponse(false);
          }
        }
      } catch (error) {
        console.error('Enterprise tech sync failed:', error);
      } finally {
        setIsLoadingEnterpriseTech(false);
        setLlmResponseLoading(false);
        setIsLoadingAgentLlmResponse(false);
      }
    };

    fetchEnterpriseTechnology();
  }, [aiSolutionData?.id, selectedSolutionId, currentOrg, diagramId]);

  const handleToggleFavorite = useCallback(() => {
    const newFavoriteState = !isFavorite;
    setIsFavorite(newFavoriteState);
    mutate({
      variables: { v1: { id: selectedSolutionId, favorite: newFavoriteState } },
    });
    setShowFavoriteModal(false);
  }, [isFavorite, mutate, selectedSolutionId]);

  useEffect(() => {
    if (currentSolutionIdRef.current !== selectedSolutionId) {
      currentSolutionIdRef.current = selectedSolutionId;
      hasLoadedImplementationRef.current = false;
      hasFetchedEnterpriseTechRef.current = false;
      setDataSources('');
      setImplementationConsiderations('');
      setSource([]);

      // Reset admin sync state
      setAgentClickCount(0);
      setLastClickTime(0);
      setIsManualSyncLoading(false);

      // Reset edit state and data when solution changes
      setUpdatedSolutionData(null);
      setOriginalSolutionData(null);
      setIsEditing(false);
    }
  }, [selectedSolutionId]);

  useEffect(() => {
    // Enterprise context application is not needed in this simplified component
    if (enterpriseContextApplied) {
      setEnterpriseContextApplied(false);
    }
  }, [enterpriseContextApplied, setEnterpriseContextApplied]);
  //  const IMPLEMENTATION_STYLES: Record<string, { bg: string; color: string; icon: string }> = {
  //   'Client Specific': {
  //     bg: '#F1FFEE',
  //     color: '#27742A',
  //     icon: '/images/client_specific.svg',
  //   },
  //   Embedded: {
  //     bg: '#FFF7F3',
  //     color: '#9A5A37',
  //     icon: '/images/embedded_icon.svg',
  //   },
  //   Extended: {
  //     bg: '#FFF7DF',
  //     color: '#B18600',
  //     icon: '/images/extended_icon.svg',
  //   },
  // };

  // const renderInfoBox = ({
  //   title,
  //   titleColor,
  //   icon,
  //   text,
  //   bg,
  //   extraSx = {},
  // }: {
  //   title: string;
  //   titleColor: string;
  //   icon: string;
  //   text: string;
  //   bg: string;
  //   extraSx?: object;
  // }) => {
  //   return (
  //     <Box
  //       sx={{
  //         background: bg,
  //         borderRadius: '6px',
  //         padding: '12px',
  //         mt: '18px',
  //         width: { xs: '100%', sm: '50%' },
  //         minHeight: '126px',
  //         ...extraSx,
  //       }}
  //     >
  //       <Box sx={{ display: 'flex' }}>
  //         <img src={icon} alt="icon" />
  //         <Typography sx={{ fontWeight: 500, fontSize: '15px', color: titleColor, ml: '8px' }}>
  //           {title}
  //         </Typography>
  //       </Box>
  //       <Typography sx={{ fontSize: '15px', fontWeight: 400, mt: '10px' }}>{text}</Typography>
  //     </Box>
  //   );
  // };

  useEffect(() => () => setAiSolutionData?.(null), [setAiSolutionData]);

  const getSvgPathforroi = (type: string) => {
    switch (type) {
      case 'HIGH':
        return '/images/GreenDot.svg';
      case 'MEDIUM':
        return '/images/YellowDot.svg';
      case 'LOW':
        return '/images/RedDot.svg';
      default:
        return '/images/GreenDot.svg';
    }
  };
  const getSvgPathForComplexity = (type: string) => {
    switch (type) {
      case 'HIGH':
        return '/images/RedDot.svg';
      case 'MEDIUM':
        return '/images/YellowDot.svg';
      case 'LOW':
        return '/images/GreenDot.svg';
      default:
        return '/images/GreenDot.svg';
    }
  };

  const handleEdit = (type: EditType, item?: any) => {
    setEditItem(item || null);
    setOpenEditModal(true);
    setEditType(type);
  };

  const handleDiscard = () => {
    // Reset to original data - use deep copy of original solution data
    if (originalSolutionData) {
      setUpdatedSolutionData(JSON.parse(JSON.stringify(originalSolutionData)));
    } else {
      setUpdatedSolutionData(aiSolutionData);
    }

    // Close the edit modal and reset all edit-related states
    setOpenEditModal(false);
    setIsEditing(false);
    setEditItem(null);
    setEditType('');
  };

  const handleSaveSolution = (data: any) => {
    handleUpdateSolution(data);
  };

  const handleSaveClick = async () => {
    // Call parent's save handler which will update data and close edit mode
    handleSave(updatedSolutionData);

    // API call to send for approval
    if (aiSolutionData.status === 'approved') {
      const url = `${API_BASE}/api/v1/e2e_taxonomy/new_ai_opportunity/${selectedSolutionId}/`;
      await httpService(httpConstants.METHOD_TYPE.PATCH, url, {
        status: 'approval_pending',
        isAddOpportunityStatusHistory: true,
        organization_id: currentOrg,
      });
    }
    setApprovalStatus('approval_pending');
    setIsEditing(false);
  };

  // Manual sync function for admin triple-click
  const triggerManualSync = useCallback(async () => {
    if (!aiSolutionData || !selectedSolutionId || isManualSyncLoading) return;

    // Use selectedSolutionId as source of truth and convert to string for cache consistency
    const solutionId = String(selectedSolutionId);

    // Verify aiSolutionData.id matches selectedSolutionId to prevent cache mismatch
    if (String(aiSolutionData.id) !== solutionId) {
      console.warn(
        `⚠️ Manual sync: Solution ID mismatch: aiSolutionData.id (${aiSolutionData.id}) !== selectedSolutionId (${selectedSolutionId}). Aborting.`
      );
      return;
    }
    setIsManualSyncLoading(true);
    console.log('🔄 Admin manual sync triggered');

    try {
      const enterpriseTechResponse = await getEnterpriseTechnology(currentOrg);
      const additionalPayload = {
        llm_agent: aiSolutionData?.llm_agent,
        solution_enabler: aiSolutionData?.solution_enabler,
        diagram_id: diagramId,
        organization_id: currentOrg,
        type: ENVIRONMENT_TYPE,
      };

      // Step 1: Call data sources sync (AGENT_URL_1)
      const dataSourcesResponse = await makeDataSourcesSyncCall(
        aiSolutionData,
        enterpriseTechResponse,
        additionalPayload,
        processType
      );

      // Step 2: Update state immediately after first call completes
      const firstResponseData =
        dataSourcesResponse?.application_in_use || dataSourcesResponse?.ai_solution || [];

      // Update both states to ensure data is available
      setLlmResponseData(firstResponseData);

      setUpdatedSolutionData((prev: any) => {
        const base = prev || aiSolutionData || {};
        // CRITICAL: Preserve ALL existing agent fields (embedded, extended, custom, enablement_matrix)
        // to prevent sections from disappearing during intermediate update
        return {
          ...base,
          agent: {
            ...base.agent,
            // Preserve existing agent structure
            embedded: base.agent?.embedded || aiSolutionData?.agent?.embedded || [],
            extended: base.agent?.extended || aiSolutionData?.agent?.extended || [],
            custom: base.agent?.custom || aiSolutionData?.agent?.custom || [],
            ai_enablement: base.agent?.ai_enablement || aiSolutionData?.agent?.ai_enablement,
            agent_classification:
              base.agent?.agent_classification || aiSolutionData?.agent?.agent_classification || [],
            // Update only ai_solution
            ai_solution: firstResponseData,
          },
        };
      });

      // Hide skeleton for data sources now that first call is complete
      // React will batch these state updates together
      setLlmResponseLoading(false);

      // Step 3: Call AI enablement sync (AGENT_URL_2)
      const enablementResponse = await makeAiEnablementSyncCall(
        aiSolutionData,
        enterpriseTechResponse,
        additionalPayload,
        processType
      );

      // Step 4: Merge both responses and update final state
      const combinedResponse = mergeSyncResponses(dataSourcesResponse, enablementResponse);

      // Map sync response to frontend state - properly extract enablement_matrix
      const agentResponseData =
        combinedResponse?.agent_classification ||
        combinedResponse?.ai_enablement?.enablement_matrix?.embedded ||
        combinedResponse?.embedded ||
        [];
      setAgentLlmResponse(agentResponseData);

      // Update with combined response (may include additional data from second call)
      setLlmResponseData(
        combinedResponse?.ai_solution || combinedResponse?.application_in_use || []
      );

      // Update the full agent data structure without dropping other fields
      setUpdatedSolutionData((prev: any) => {
        const merged = mergeAgentSyncData(prev || aiSolutionData, combinedResponse);
        setAiSolutionData?.(merged);

        // Cache the merged data for future use (manual sync always caches)
        const hasCompleteAgentData =
          merged?.agent?.ai_enablement &&
          (merged?.agent?.ai_solution || merged?.agent?.application_in_use);
        if (hasCompleteAgentData) {
          globalAgentDataCache.set(solutionId, {
            solutionData: merged,
            agentData: {
              agentLlmResponse: agentResponseData,
              llmResponseData:
                combinedResponse?.ai_solution || combinedResponse?.application_in_use || [],
            },
          });
          globalSyncedSolutions.set(solutionId, {
            timestamp: Date.now(),
            wasManualSync: true,
            hasAgentData: true,
          });
        }

        return merged;
      });

      console.log('✅ Admin manual sync completed successfully');
    } catch (error) {
      console.error('❌ Admin manual sync failed:', error);
      // Hide skeleton on error
      setLlmResponseLoading(false);
    } finally {
      setIsManualSyncLoading(false);
    }
  }, [aiSolutionData, currentOrg, diagramId, isManualSyncLoading, processType]);

  // Handle triple-click on Agents tab for admin sync
  const handleAgentTabClick = useCallback(
    (tabKey: string) => {
      const currentTime = Date.now();

      if (tabKey === 'agents') {
        // Reset count if more than 2 seconds have passed
        if (currentTime - lastClickTime > 2000) {
          setAgentClickCount(1);
          console.log('🔄 Admin sync: First click on Agents tab');
        } else {
          const newCount = agentClickCount + 1;
          setAgentClickCount(newCount);
          console.log(`🔄 Admin sync: Click ${newCount}/3 on Agents tab`);
        }

        setLastClickTime(currentTime);

        // Trigger manual sync on 3rd click
        if (agentClickCount + 1 === 3) {
          console.log('🚀 Admin sync: Triple-click detected! Triggering manual sync...');
          triggerManualSync();
          setAgentClickCount(0); // Reset count
        }
      } else {
        // Reset count if clicking on other tabs
        if (agentClickCount > 0) {
          console.log('🔄 Admin sync: Switched tabs, resetting click count');
          setAgentClickCount(0);
        }
      }

      // Always update the active piece
      setActivePiece(tabKey);
    },
    [agentClickCount, lastClickTime, triggerManualSync]
  );

  useEffect(() => {
    return () => {
      setActivePiece('information');
    };
  }, []);

  const handleEnablerText = (input: any) => {
    if (typeof input === 'string' && input.startsWith('[') && input.endsWith(']')) {
      const extractedValues = JSON.parse(input.replace(/'/g, '"')).join(', ');
      return extractedValues;
    } else return input;
  };

  // Use the appropriate data source based on edit mode
  const displayData = updatedSolutionData || aiSolutionData || lastValidDisplayDataRef.current;

  useEffect(() => {
    if (updatedSolutionData || aiSolutionData) {
      lastValidDisplayDataRef.current = updatedSolutionData || aiSolutionData;
    }
  }, [updatedSolutionData, aiSolutionData]);

  // Derive isIdeation from displayData.agent.aiSolution if present, otherwise use prop
  const isIdeationDerived = useMemo(() => {
    if (fromList) {
      return displayData?.subprocess_type === 'custom';
    } else {
      return isIdeation;
    }
  }, [displayData, isIdeation, fromList]);

  // Use derived isIdeation value throughout the component
  const effectiveIsIdeation = isIdeationDerived;

  // Check if agent data exists in the API response
  // const hasAgentDataInResponse =
  //   aiSolutionData?.agent &&
  //   (aiSolutionData.agent.ai_enablement ||
  //     aiSolutionData.agent.agent_classification ||
  //     aiSolutionData.agent.embedded ||
  //     aiSolutionData.agent.extended ||
  //     aiSolutionData.agent.custom);

  const defaultAgentData = { embedded: [], extended: [], custom: [] };

  const normalizedAiSolutionData = (dataInput?: any) => {
    // Safety check: ensure we have data to work with
    if (!updatedAiSolution && !aiSolutionData) {
      return defaultAgentData;
    }

    // Prefer explicit input, then fall back to current displayData
    const data = dataInput || displayData || {};
    const agent = data?.agent;

    // Safety check: ensure agent exists
    if (!agent) {
      return defaultAgentData;
    }

    try {
      if (window.location.pathname.includes('ideation')) {
        // For ideation: get enablement_matrix structure
        const enablementMatrix = agent?.ai_enablement?.enablement_matrix;

        // Validate that enablementMatrix has the expected structure
        if (enablementMatrix && typeof enablementMatrix === 'object') {
          const { embedded = [], extended = [], custom = [] } = enablementMatrix;
          return {
            embedded: Array.isArray(embedded) ? embedded : [],
            extended: Array.isArray(extended) ? extended : [],
            custom: Array.isArray(custom) ? custom : [],
          };
        }
        return defaultAgentData;
      } else if (window.location.pathname.includes('process-xplr')) {
        // For process-xplr: transform data into embedded/extended/custom buckets
        const agentdata: {
          embedded: { capability_name: string; description: string }[];
          extended: { capability_name: string; description: string }[];
          custom: { capability_name: string; description: string }[];
        } = { embedded: [], extended: [], custom: [] };

        const mapMatrixItem = (item: any) => ({
          capability_name: item?.capability_name || item?.agent_name || '',
          description: item?.description || item?.justification || '',
        });

        // Step 1: Extract from enablement_matrix if available
        const enablementMatrix = agent?.ai_enablement?.enablement_matrix;
        if (enablementMatrix && typeof enablementMatrix === 'object') {
          const {
            embedded: embeddedMatrix = [],
            extended: extendedMatrix = [],
            custom: customMatrix = [],
          } = enablementMatrix;

          if (Array.isArray(embeddedMatrix)) {
            embeddedMatrix.forEach((item: any) => {
              const mapped = mapMatrixItem(item);
              if (mapped.capability_name) agentdata.embedded.push(mapped);
            });
          }

          if (Array.isArray(extendedMatrix)) {
            extendedMatrix.forEach((item: any) => {
              const mapped = mapMatrixItem(item);
              if (mapped.capability_name) agentdata.extended.push(mapped);
            });
          }

          if (Array.isArray(customMatrix)) {
            customMatrix.forEach((item: any) => {
              const mapped = mapMatrixItem(item);
              if (mapped.capability_name) agentdata.custom.push(mapped);
            });
          }
        }

        // Step 2: Also check fallback agent.embedded array and COMBINE with enablement_matrix data
        // This ensures we don't lose data if enablement_matrix is incomplete/empty
        const embeddedArray = agent?.embedded;
        if (Array.isArray(embeddedArray)) {
          embeddedArray.forEach((item: any) => {
            const implementationType = item?.implementation_type || '';
            const agentName = item?.agent_name || '';
            const justification = item?.justification || '';

            if (!agentName) return;

            // Check if this agent is already in the array (avoid duplicates)
            const existingInEmbedded = agentdata.embedded.some(
              (existing) => existing.capability_name.toLowerCase() === agentName.toLowerCase()
            );
            const existingInExtended = agentdata.extended.some(
              (existing) => existing.capability_name.toLowerCase() === agentName.toLowerCase()
            );
            const existingInCustom = agentdata.custom.some(
              (existing) => existing.capability_name.toLowerCase() === agentName.toLowerCase()
            );

            if (existingInEmbedded || existingInExtended || existingInCustom) {
              return; // Skip duplicates
            }

            const newItem = {
              capability_name: agentName,
              description: justification,
            };

            if (implementationType === 'Extended') {
              agentdata.extended.push(newItem);
            } else if (implementationType === 'Embedded') {
              agentdata.embedded.push(newItem);
            } else if (implementationType === 'Client Specific') {
              agentdata.custom.push(newItem);
            }
          });
        }

        // Step 3: Check direct agent arrays as final fallback (combine, don't replace)
        if (Array.isArray(agent?.embedded) && agent.embedded.length > 0) {
          agent.embedded.forEach((item: any) => {
            if (item?.agent_name) {
              const exists =
                agentdata.embedded.some(
                  (e) => e.capability_name.toLowerCase() === item.agent_name.toLowerCase()
                ) ||
                agentdata.extended.some(
                  (e) => e.capability_name.toLowerCase() === item.agent_name.toLowerCase()
                ) ||
                agentdata.custom.some(
                  (e) => e.capability_name.toLowerCase() === item.agent_name.toLowerCase()
                );
              if (!exists) {
                agentdata.embedded.push({
                  capability_name: item.agent_name,
                  description: item.justification || '',
                });
              }
            }
          });
        }

        if (Array.isArray(agent?.extended) && agent.extended.length > 0) {
          agent.extended.forEach((item: any) => {
            if (item?.agent_name || item?.capability_name) {
              const name = item.agent_name || item.capability_name;
              const exists =
                agentdata.embedded.some(
                  (e) => e.capability_name.toLowerCase() === name.toLowerCase()
                ) ||
                agentdata.extended.some(
                  (e) => e.capability_name.toLowerCase() === name.toLowerCase()
                ) ||
                agentdata.custom.some(
                  (e) => e.capability_name.toLowerCase() === name.toLowerCase()
                );
              if (!exists) {
                agentdata.extended.push({
                  capability_name: name,
                  description: item.justification || item.description || '',
                });
              }
            }
          });
        }

        if (Array.isArray(agent?.custom) && agent.custom.length > 0) {
          agent.custom.forEach((item: any) => {
            if (item?.agent_name || item?.capability_name) {
              const name = item.agent_name || item.capability_name;
              const exists =
                agentdata.embedded.some(
                  (e) => e.capability_name.toLowerCase() === name.toLowerCase()
                ) ||
                agentdata.extended.some(
                  (e) => e.capability_name.toLowerCase() === name.toLowerCase()
                ) ||
                agentdata.custom.some(
                  (e) => e.capability_name.toLowerCase() === name.toLowerCase()
                );
              if (!exists) {
                agentdata.custom.push({
                  capability_name: name,
                  description: item.justification || item.description || '',
                });
              }
            }
          });
        }

        // Log if normalization returns incomplete data (all empty)
        const hasData =
          agentdata.embedded.length > 0 ||
          agentdata.extended.length > 0 ||
          agentdata.custom.length > 0;
        if (!hasData && agent) {
          console.warn('⚠️ Normalization returned empty arrays. Agent structure:', {
            hasEnablementMatrix: !!enablementMatrix,
            hasEmbeddedArray: Array.isArray(agent.embedded) && agent.embedded.length > 0,
            hasExtendedArray: Array.isArray(agent.extended) && agent.extended.length > 0,
            hasCustomArray: Array.isArray(agent.custom) && agent.custom.length > 0,
            agentKeys: Object.keys(agent),
          });
        }

        return agentdata;
      }

      return defaultAgentData;
    } catch (error) {
      console.error('Error normalizing AI solution data:', error);
      return defaultAgentData;
    }
  };

  const [normalizedAgentData, setNormalizedAgentData] = useState<{
    embedded: CardData[];
    extended: CardData[];
    custom: CardData[];
  }>(defaultAgentData);

  // Ref to track previous normalized state for fallback during incomplete sync states
  const previousNormalizedDataRef = useRef<{
    embedded: CardData[];
    extended: CardData[];
    custom: CardData[];
  }>(defaultAgentData);

  useEffect(() => {
    if (!displayData) {
      setNormalizedAgentData(defaultAgentData);
      return;
    }

    // Check if we're in an intermediate sync state (has ai_solution but missing agent structure)
    const isIntermediateSyncState =
      isIdeationEnablementLoading &&
      displayData?.agent?.ai_solution &&
      !displayData?.agent?.embedded?.length &&
      !displayData?.agent?.extended?.length &&
      !displayData?.agent?.custom?.length &&
      !displayData?.agent?.ai_enablement?.enablement_matrix;

    // If in intermediate state, use previous normalized data to prevent sections from disappearing
    if (isIntermediateSyncState) {
      console.log('🛡️ Guard: Using previous normalized data during intermediate sync state');
      setNormalizedAgentData(previousNormalizedDataRef.current);
      return;
    }

    const normalized = normalizedAiSolutionData(displayData);

    // Only update previous ref if normalized data has content (not empty)
    const hasContent =
      normalized.embedded.length > 0 ||
      normalized.extended.length > 0 ||
      normalized.custom.length > 0;
    if (hasContent) {
      previousNormalizedDataRef.current = normalized;
    }

    setNormalizedAgentData(normalized);
  }, [displayData, isIdeationEnablementLoading]);

  // Data Requirements rendering function
  const renderDataRequirementsContent = useMemo(() => {
    type DataSource = { name: string; type: string; technologies?: string[] };

    // Parse description_llm safely
    let descriptionAssets: { data_asset: string; technology: string[] }[] = [];
    try {
      const aiSolution = displayData?.agent?.ai_solution;
      descriptionAssets = llmResponseData && Array.isArray(aiSolution) ? aiSolution : [];
    } catch (err) {
      console.error('Invalid description_llm JSON', err);
      descriptionAssets = [];
    }

    // Helper to transform arrays into {name, type} while preserving technologies from description_llm
    const transformSources = (items: string[] | undefined, type: string): DataSource[] =>
      (items || []).map((name) => {
        const matched = descriptionAssets.find(
          (a) => a.data_asset?.toLowerCase() === name.toLowerCase()
        );

        return {
          name,
          type,
          technologies: Array.isArray(matched?.technology) ? matched.technology : [],
        };
      });

    // Use data_sources from displayData response
    const internalSources: DataSource[] = [
      ...transformSources(displayData?.data_sources?.internal?.structured, 'Structured'),
      ...transformSources(displayData?.data_sources?.internal?.unstructured, 'Unstructured'),
    ];

    const externalSources: DataSource[] = [
      ...transformSources(displayData?.data_sources?.external?.structured, 'Structured'),
      ...transformSources(displayData?.data_sources?.external?.unstructured, 'Unstructured'),
    ];

    const renderChip = (label: string) => (
      <Typography
        key={label}
        variant="caption"
        sx={{
          borderRadius: '12px',
          px: 1.5,
          py: 0.5,
          borderWidth: '0.5px',
          borderStyle: 'solid',
          borderColor: '#DDE1E6',
          fontSize: '12px',
          lineHeight: '14px',
          color: 'text.primary',
          textAlign: 'center',
          ml: 1,
        }}
      >
        {label}
      </Typography>
    );

    const renderSourceItem = (item: DataSource, sourceCategory: 'internal' | 'external') => {
      return (
        <Box
          key={`${item.name}`}
          sx={{ mb: 1, display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'nowrap' }}>
            <img
              src="/images/db.svg"
              alt=""
              style={{ width: 16, height: 16, padding: 4, marginTop: 2 }}
            />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '16px', mr: 1 }}>
              {item.name}
            </Typography>
          </Box>
          {renderChip(item.type)}
          {llmResponseLoading ? (
            <Typography
              sx={{
                borderRadius: '12px',
                px: 1,
                borderWidth: '0.5px',
                borderStyle: 'solid',
                borderColor: '#DDE1E6',
                fontSize: '12px',
                lineHeight: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                ml: 1,
                height: '22px',
              }}
            >
              <Skeleton variant="rectangular" height={8} width={70} sx={{ borderRadius: '12px' }} />
            </Typography>
          ) : (
            <Typography sx={{ mt: '4px' }}>
              {item.technologies?.map((tech) => renderChip(tech))}
            </Typography>
          )}
          {isEditing && (
            <DriveFileRenameOutlineIcon
              sx={{ cursor: 'pointer', height: '16px', width: '16px', color: 'grey.500' }}
              onClick={() =>
                handleEdit('data_requirements', {
                  name: item.name,
                  originalName: item.name,
                  type: item.type,
                  sourceCategory: sourceCategory,
                  technologies: item.technologies,
                })
              }
            />
          )}
        </Box>
      );
    };

    if (internalSources.length || externalSources.length) {
      return (
        <Grid container direction="column">
          {internalSources.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '16px', mb: 2 }}>
                Internal Data Sources
              </Typography>
              {internalSources.map((item) => renderSourceItem(item, 'internal'))}
            </Box>
          )}

          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '16px', mb: 2 }}>
              External Data Sources
            </Typography>
            {externalSources.length > 0 ? (
              externalSources.map((item) => renderSourceItem(item, 'external'))
            ) : (
              <Typography variant="body2" sx={{ fontWeight: 400, fontSize: '14px' }}>
                None
              </Typography>
            )}
          </Box>
        </Grid>
      );
    }

    if (isLoadingImplementation) {
      return (
        <Grid container spacing={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {[...Array(8)].map((_, index) => (
              <Box key={index} sx={{ height: '1.5em', bgcolor: 'grey.200', borderRadius: 1 }} />
            ))}
          </Box>
        </Grid>
      );
    }

    return (
      <Grid container spacing={4}>
        <Typography>No data requirements available</Typography>
      </Grid>
    );
  }, [displayData, isLoadingImplementation, llmResponseLoading, llmResponseData, isEditing]);

  const renderImplementationConsiderationsContent = useMemo(() => {
    const keyConsiderationsList = displayData?.key_requirements || [];

    if (keyConsiderationsList.length) {
      return (
        <Grid container spacing={4}>
          <Grid size={{ xs: 12, md: 12 }}>
            <Box>
              {keyConsiderationsList.map((item: string, idx: number) => (
                <Box
                  key={idx}
                  sx={{ display: 'flex', alignItems: 'flex-start', my: 1, minWidth: 0 }}
                >
                  <Box sx={{ pt: '3px', flexShrink: 0, mr: 1 }}>
                    <img src="/images/Info_implement.svg" />
                  </Box>

                  <Typography
                    variant="body1"
                    sx={{
                      display: 'block',
                      whiteSpace: 'pre-line',
                      textAlign: 'left',
                      m: 0,
                      pl: 0,
                      textIndent: 0,
                    }}
                  >
                    {item}
                    {isEditing && (
                      <DriveFileRenameOutlineIcon
                        sx={{ cursor: 'pointer', height: '16px', width: '16px', color: 'grey.500' }}
                        onClick={() => handleEdit('key_requirements', { index: idx, name: item })}
                      />
                    )}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>
      );
    }

    return (
      <Grid container spacing={4}>
        <Grid size={{ xs: 12, md: 12 }}>
          <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
            No key requirements available
          </Typography>
        </Grid>
      </Grid>
    );
  }, [displayData?.key_requirements, isLoadingImplementation, isEditing]);

  if (queryLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '80vh',
          width: '100%',
        }}
      >
        <Loader message="Fetching AI Opportunity" />
      </Box>
    );
  }

  if (queryError) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}
      >
        <Typography color="error">Error loading solution data</Typography>
      </Box>
    );
  }

  if (!displayData || !selectedSolutionId) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '80vh',
          width: '100%',
        }}
      >
        <Typography>No solution data available. Please select a solution.</Typography>
      </Box>
    );
  }

  return (
    <>
      {/* Breadcrumb Header - Only show when coming from AiSolutionsTaxonomy */}
      {processName && breadcrumb && breadcrumb.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            p: 2,
            borderBottom: !isProcessAiOpportunities ? '1px solid #E5E7EB' : 'none',
            backgroundColor: '#FFFFFF',
          }}
        >
          {/* Breadcrumb */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            {breadcrumb.map((item, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography
                  sx={{
                    fontSize: '14px',
                    color: '#737373',
                    fontWeight: 400,
                  }}
                >
                  {item}
                </Typography>
                {index < breadcrumb.length - 1 && (
                  <Typography sx={{ fontSize: '14px', color: '#737373' }}>/</Typography>
                )}
              </Box>
            ))}
          </Box>

          {/* Process Name */}
          <Typography
            sx={{
              fontSize: '26px',
              fontWeight: 600,
              color: '#313131',
            }}
          >
            {processName}
          </Typography>
        </Box>
      )}
      {/* Show title only when NOT coming from AiSolutionsTaxonomy (isProcessAiOpportunities) */}
      {!isProcessAiOpportunities && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderBottom: '1px solid #e0e0e0',
          }}
        >
          <Typography variant="h6">
            {effectiveIsIdeation ? 'AI Ideation Opportunity Details' : 'AI Opportunity Details'}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {isEditable && isProcessDiagramActive ? (
              <>
                {/* Show approval controls only when NOT in editing mode */}
                {!isEditing && isProcessDiagramActive && (
                  <>
                    {(approvalStatus === 'draft' || approvalStatus === 'created') && (
                      <Button
                        variant="text"
                        onClick={handleSendForApproval}
                        sx={{
                          textTransform: 'none',
                          color: '#1A55F9',
                          fontWeight: 500,
                          px: 2,
                          minWidth: 0,
                        }}
                        startIcon={<CheckCircleOutline />}
                      >
                        Send for approval
                      </Button>
                    )}
                  </>
                )}

                {!isEditing ? (
                  <Box
                    sx={{
                      color: '#1A55F9',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <img src="/images/edit-border.png" alt="edit" width={14} />
                    <Typography
                      variant="button"
                      sx={{ textTransform: 'capitalize' }}
                      onClick={() => setIsEditing(true)}
                    >
                      Edit
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button variant="outlined" size="small" onClick={handleDiscard}>
                      Discard
                    </Button>
                    <Button variant="contained" size="small" onClick={handleSaveClick}>
                      Save
                    </Button>
                  </Box>
                )}
                {!isEditing && isProcessDiagramActive && (approvalStatus === 'draft' || approvalStatus === 'created') && (
                  <Button
                    variant="text"
                    onClick={() => setShowDeleteConfirmModal(true)}
                    sx={{
                      textTransform: 'none',
                      color: '#EF4444',
                      fontWeight: 500,
                      px: 2,
                      minWidth: 0,
                    }}
                    startIcon={<img src="/images/removeOpportunity.svg" alt="delete" />}
                  >
                    Remove
                  </Button>
                )}
              </>
            ) : null}
            {!isProcessDiagramActive && (
              <Button
                variant="outlined"
                startIcon={<img src="/images/Vault.png" alt="vault" />}
                onClick={handleVaultClick}
                disabled={isVaultActionLoading}
                sx={{
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                {`${(localVaultState ?? selectedSolutionFromLibrary?.vault ?? aiSolutionData?.vault) ? 'Remove from' : 'Add to'} my list`}{' '}
                {isVaultActionLoading && <span style={{ marginLeft: 8, opacity: 0.7 }}>…</span>}
              </Button>
            )}
            {closeDrawer && <CloseIcon onClick={closeDrawer} sx={{ cursor: 'pointer' }} />}
          </Box>
        </Box>
      )}

      <Box
        sx={{
          p: isProcessAiOpportunities ? 3 : 0,
          paddingTop: isProcessAiOpportunities ? 0 : 0,
        }}
      >
        <Box
          sx={{
            border: isProcessAiOpportunities ? '1px solid #E5E7EB' : 'none', // lighter border
            borderRadius: isProcessAiOpportunities ? '10px' : 'none',
            p: isProcessAiOpportunities ? 1 : 0,
          }}
        >
          <Box
            sx={{
              mx: 2,
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              border: !isProcessAiOpportunities ? '1px solid #e0e0e0' : 'none',
              borderRadius: 1,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <Typography variant="caption" sx={{ color: '#1A55F9', fontWeight: 600 }}>
                {handleEnablerText(displayData?.solution_enabler)}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Typography variant="h6">{displayData?.opportunity_name}</Typography>
                {isEditing && (
                  <DriveFileRenameOutlineIcon
                    sx={{
                      cursor: 'pointer',
                      height: '16px',
                      width: '16px',
                      color: 'grey.500',
                    }}
                    onClick={() => handleEdit('name')}
                  />
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      background: '#ffffff',
                      borderRadius: '39px',
                      border: '1.5px solid #1C53F5',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: 2,
                      height: '28px',
                      boxShadow: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#1B3FD1',
                        fontFamily: 'Poppins',
                        fontWeight: 500,
                        fontSize: '14px',
                        lineHeight: '120%',
                        textTransform: 'uppercase',
                      }}
                    >
                      {String(displayData?.impact)}
                    </Typography>
                  </Box>

                  {isEditing && (
                    <DriveFileRenameOutlineIcon
                      sx={{
                        cursor: 'pointer',
                        fontSize: 18,
                        color: 'grey.500',
                        mt: '2px',
                      }}
                      onClick={() => handleEdit('impact')}
                    />
                  )}
                </Box>
              </Box>
            </Box>

            {/* Right side: Impact badge and action buttons */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                ml: 2,
              }}
            >
              {!isProcessDiagramActive && (
                <>
                  {isMobile && (
                    <IconButton onClick={() => handleToggleVault()}>
                      <img src="/images/Vault.png" alt="vault" style={{ width: 24, height: 24 }} />
                    </IconButton>
                  )}
                </>
              )}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                {isIdeation && (
                  <>
                    {(() => {
                      const statusConfig = getApprovalStatusConfig(approvalStatus);
                      if (!statusConfig.show) return null;
                      return (
                        <Chip
                          label={statusConfig.text}
                          sx={{
                            height: '28px',
                            borderRadius: '4px',
                            fontFamily: 'Poppins',
                            fontWeight: 500,
                            fontSize: '12px',
                            color: statusConfig.color,
                            backgroundColor: statusConfig.bgColor,
                            border: 'none',
                            '& .MuiChip-label': {
                              padding: '1px 6px',
                            },
                          }}
                        />
                      );
                    })()}
                  </>
                )}
                {/* {!effectiveIsIdeation && (
                  <Box
                    sx={{
                      background: 'linear-gradient(90deg, #1532B8 24.06%, #1B54F8 100%)',
                      borderRadius: '39px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: { xs: '50%', md: '161px' },
                      height: '33px',
                      opacity: 1,
                      boxShadow: 1,
                      mr: { md: !isEditing ? 3 : 0 },
                      mt: { xs: 2, md: 0 },
                    }}
                  >
                    <Typography
                      sx={{
                        color: 'white',
                        fontFamily: 'Poppins',
                        fontWeight: 500,
                        fontSize: '16px',
                        lineHeight: '120%',
                        letterSpacing: '-1%',
                        textAlign: 'center',
                        textTransform: 'uppercase',
                      }}
                    >
                      {String(displayData?.impact)}
                    </Typography>
                  </Box>
                )} */}
                {isEditing && !effectiveIsIdeation && processType !== 'custom' && (
                  <DriveFileRenameOutlineIcon
                    sx={{
                      cursor: 'pointer',
                      height: '16px',
                      width: '16px',
                      color: 'grey.500',
                    }}
                    onClick={() => handleEdit('impact')}
                  />
                )}
              </Box>
              {/* Show vault button in solution card when on desktop and coming from AiSolutionsTaxonomy */}
              {isProcessAiOpportunities && !isMobile && (
                <Button
                  variant="outlined"
                  startIcon={<img src="/images/Vault.png" alt="vault" />}
                  onClick={handleVaultClick}
                  disabled={isVaultActionLoading}
                  sx={{
                    textTransform: 'none',
                    fontSize: '12px',
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                    minWidth: 'auto',
                    px: 1.5,
                  }}
                >
                  {`${localVaultState ? 'Remove from' : 'Add to'} my list`}{' '}
                  {isVaultActionLoading && <span style={{ marginLeft: 8, opacity: 0.7 }}>…</span>}
                </Button>
              )}
            </Box>
          </Box>
          <Grid container spacing={2} sx={{ p: 2 }}>
            <Grid size={{ xs: 12, md: 9 }} spacing={2}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <CardContent
                  sx={{
                    border: '1px solid #DDE1E6',
                    boxShadow: 'none',
                    borderRadius: '10px',
                    mb: 2,
                    p: 0,
                  }}
                >
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    {[
                      { key: 'information', label: 'Key Information' },
                      {
                        key: 'agents',
                        label:
                          effectiveIsIdeation && !isProcessAiOpportunities
                            ? 'AI Automation'
                            : processType === 'custom'
                              ? 'AI Automation'
                              : 'Agents',
                      },
                    ].map((tab, index) => (
                      <Box
                        key={tab.key}
                        sx={{
                          flex: 1,
                          height: '43px',
                          width: '100%',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          fontSize: '16px',
                          cursor: 'pointer',
                          fontWeight: 600,
                          borderLeft: index === 1 ? '1px solid #DDE1E6' : 'none',
                        }}
                        onClick={() => handleAgentTabClick(tab.key)}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            color: activePiece === tab.key ? '#1A55F9' : '#000000',
                            borderBottom: activePiece === tab.key ? '2px solid #1A55F9' : 'none',
                            pb: activePiece !== tab.key ? '2px' : 0,
                            fontSize: '16px',
                          }}
                        >
                          {tab.label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>

                {activePiece === 'information' ? (
                  <Grid
                    container
                    spacing={2}
                    sx={{
                      height: 'calc(100vh - 270px)',
                      overflow: 'hidden',
                    }}
                  >
                    <Grid
                      size={{ xs: 12, md: 12 }}
                      sx={{
                        height: '100%',
                        overflowY: 'auto',
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                        '&::-webkit-scrollbar': {
                          display: 'none',
                        },
                        // pr: 1,
                      }}
                    >
                      <Card
                        sx={{
                          mb: 2,
                          boxShadow: 'none',
                          border: !isProcessAiOpportunities ? '1px solid #E0E0E0' : 'none',
                        }}
                      >
                        <CardContent sx={{ padding: isProcessAiOpportunities ? 0 : 2 }}>
                          {/* OVERVIEW */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography sx={{ fontWeight: 600, fontSize: '18px' }}>
                              Overview
                            </Typography>
                            {isEditing && (
                              <DriveFileRenameOutlineIcon
                                sx={{ cursor: 'pointer', fontSize: 18, color: 'grey.600' }}
                                onClick={() => handleEdit('overview')}
                              />
                            )}
                          </Box>
                          <Typography sx={{ fontSize: '14px', lineHeight: 1.6 }}>
                            {displayData?.description}
                          </Typography>

                          {/* PERFORMANCE IMPROVEMENT */}
                          {displayData?.performance_improvement && (
                            <Box sx={{ mt: 3 }}>
                              <Typography sx={{ fontWeight: 600, mb: 1, fontSize: '18px' }}>
                                Performance Improvement Detail
                              </Typography>

                              <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                                Supports Performance Improvement :
                              </Typography>

                              <Stack spacing={1} mt={1}>
                                {(
                                  displayData?.performance_improvement
                                    ?.supports_performance_improvement || []
                                ).map((item: string, i: number) => (
                                  <Typography
                                    key={i}
                                    sx={{
                                      bgcolor: '#F3F6FF',
                                      minHeight: '36px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      px: 1.5,
                                      py: 0.5,
                                      borderRadius: '4px',
                                      width: 'full',
                                      fontWeight: 500,
                                      fontSize: '14px',
                                    }}
                                  >
                                    {item}
                                  </Typography>
                                ))}
                              </Stack>
                            </Box>
                          )}

                          {/* VALUE DRIVER */}
                          {displayData?.performance_improvement?.value_driver && (
                            <Box sx={{ mt: 3 }}>
                              <Typography sx={{ fontWeight: 600, mb: 1, fontSize: '14px' }}>
                                Value Driver
                              </Typography>

                              <Typography sx={{ fontSize: '14px', lineHeight: 1.6 }}>
                                {displayData.performance_improvement.value_driver}
                              </Typography>
                            </Box>
                          )}

                          <Divider sx={{ my: 1 }} />

                          {/* ROI + COMPLEXITY */}
                          <Grid container alignItems="stretch">
                            {/* ROI */}
                            <Grid size={{ xs: 12, md: 5.9 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography sx={{ fontSize: '14px', color: '#666' }}>
                                  ROI
                                </Typography>

                                {isEditing && (
                                  <DriveFileRenameOutlineIcon
                                    sx={{ cursor: 'pointer', fontSize: 16 }}
                                    onClick={() => handleEdit('roi')}
                                  />
                                )}
                              </Box>
                              <Typography
                                sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}
                              >
                                <img
                                  src={getSvgPathforroi(displayData?.roi)}
                                  alt=""
                                  style={{ width: 10, height: 10, marginRight: 3 }}
                                />
                                {displayData?.roi}
                              </Typography>
                              <Typography sx={{ fontSize: '13px' }}>
                                {displayData?.roi_description}
                              </Typography>
                            </Grid>

                            {/* VERTICAL DIVIDER */}
                            <Grid
                              size={{ xs: 12, md: 0.2 }}
                              sx={{
                                display: { xs: 'none', md: 'flex' },
                                justifyContent: 'center',
                                pr: 1,
                              }}
                            >
                              <Divider orientation="vertical" flexItem />
                            </Grid>

                            {/* COMPLEXITY */}
                            <Grid size={{ xs: 12, md: 5.9 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography sx={{ fontSize: '14px', color: '#666' }}>
                                  Complexity
                                </Typography>

                                {isEditing && (
                                  <DriveFileRenameOutlineIcon
                                    sx={{ cursor: 'pointer', fontSize: 16 }}
                                    onClick={() => handleEdit('complexity')}
                                  />
                                )}
                              </Box>
                              <Typography
                                sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}
                              >
                                <img
                                  src={getSvgPathForComplexity(displayData?.complexity)}
                                  alt=""
                                  style={{ width: 10, height: 10, marginRight: 3 }}
                                />
                                {displayData?.complexity}
                              </Typography>

                              <Typography sx={{ fontSize: '13px' }}>
                                {displayData?.complexity_description}
                              </Typography>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>

                      <Grid
                        container
                        spacing={2}
                        sx={{ flexDirection: { xs: 'column', md: 'row' } }}
                      >
                        <Grid size={{ xs: 12, md: 6 }}>
                          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                minHeight: '3rem',
                                bgcolor: '#1A55F9',
                                color: 'white',
                                justifyContent: 'space-between',
                                px: 2,
                                py: 1,
                                borderTopLeftRadius: '8px',
                                borderTopRightRadius: '8px',
                                fontWeight: 'bold',
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <i className="pi pi-bullseye" style={{ marginRight: '8px' }} />
                                <img
                                  src="/images/data_sources.svg"
                                  style={{ marginRight: '8px' }}
                                />
                                <Typography>Data Sources</Typography>
                              </Box>
                              {isEditing && (
                                <IconButton
                                  size="small"
                                  onClick={() => handleEdit('add_data_requirements')}
                                >
                                  <AddBoxOutlinedIcon sx={{ color: 'white' }} />
                                </IconButton>
                              )}
                            </Box>
                            <Stack
                              spacing={2}
                              alignItems="baseline"
                              justifyContent="flex-start"
                              sx={{ px: 3, py: 3, border: '1px solid #ccc' }}
                            >
                              {renderDataRequirementsContent}
                            </Stack>
                          </Box>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <Box
                            sx={{ height: 'max-content', display: 'flex', flexDirection: 'column' }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                minHeight: '3rem',
                                bgcolor: '#1A55F9',
                                color: 'white',
                                justifyContent: 'space-between',
                                px: 2,
                                py: 1,
                                borderTopLeftRadius: '8px',
                                borderTopRightRadius: '8px',
                                fontWeight: 'bold',
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <i className="pi pi-bullseye" style={{ marginRight: '8px' }} />
                                <img
                                  src="/images/Info_implement_info.svg"
                                  style={{ marginRight: '8px' }}
                                />
                                <Typography>Key Requirements</Typography>
                              </Box>
                              {isEditing && (
                                <IconButton
                                  size="small"
                                  onClick={() => handleEdit('add_key_requirements')}
                                >
                                  <AddBoxOutlinedIcon sx={{ color: 'white' }} />
                                </IconButton>
                              )}
                            </Box>
                            <Stack
                              spacing={2}
                              alignItems="baseline"
                              justifyContent="flex-start"
                              sx={{ flexGrow: 1, px: 3, py: 3, border: '1px solid #ccc' }}
                            >
                              {renderImplementationConsiderationsContent}
                            </Stack>
                          </Box>
                        </Grid>
                      </Grid>
                    </Grid>

                    {/* <Grid size={{ xs: 12, md: 3 }}>
                      <TopBenefits
                        allTopBenefits={
                          displayData?.benefits
                            ? Object.entries(displayData.benefits).map(([key, value]) => ({
                                title: getBenefitTitle(key),
                                label: getBenefitLabelNew(value as string),
                              }))
                            : []
                        }
                        isEditing={isEditing}
                        solutionData={updatedSolutionData}
                        setSolutionData={setUpdatedSolutionData}
                      />
                    </Grid> */}
                  </Grid>
                ) : // : isIdeationEnablementLoading ? (
                  //   <Box
                  //     sx={{
                  //       minHeight: '220px',
                  //       display: 'flex',
                  //       flexDirection: 'column',
                  //       alignItems: 'center',
                  //       justifyContent: 'center',
                  //       textAlign: 'center',
                  //       gap: 1.5,
                  //       mt: 15,
                  //     }}
                  //   >
                  //     <CircularProgress />
                  //     <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#000000' }}>
                  //       Please wait while we are generating{' '}
                  //       {isIdeation
                  //         ? 'AI Enablement'
                  //         : processType === 'custom'
                  //           ? 'AI Enablement'
                  //           : 'Agents'}
                  //     </Typography>
                  //   </Box>
                  // )
                  // agentLlmResponse.length === 0 || Object.keys(agentLlmResponse).length === 0 ? (
                  !hasEnterpriseTechApplied ? (
                    <Box
                      sx={{
                        width: '100%',
                        minHeight: '220px',
                        bgcolor: '#F6F8FC',
                        borderRadius: '8px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        gap: 1.5,
                        px: 2,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '18px',
                          fontWeight: 600,
                          color: '#000',
                        }}
                      >
                        Enablement Not Available
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: '14px',
                          color: '#5F6368',
                          maxWidth: 'full',
                          lineHeight: 1.6,
                        }}
                      >
                        Configure your technology landscape to see the enablement available for this
                        opportunity.
                      </Typography>

                      <Button
                        variant="contained"
                        sx={{
                          mt: 1,
                          backgroundColor: '#1A55F9',
                          textTransform: 'none',
                          borderRadius: '6px',
                          px: 3,
                          py: 1,
                          fontSize: '14px',
                          fontWeight: 500,
                          '&:hover': {
                            backgroundColor: '#1746d1',
                          },
                        }}
                        onClick={() => {
                          navigate('/enterprise-context');
                        }}
                      >
                        Configure Technology Landscape
                      </Button>
                    </Box>
                  ) : (
                    <Box sx={{ height: '600px', overflow: 'auto' }}>
                      {(effectiveIsIdeation || processType === 'custom') &&
                        !isProcessAiOpportunities ? (
                        // AI Enablement view for isIdeation = true or Custom process
                        <>
                          <Card sx={{ mb: 0, boxShadow: 'none' }}>
                            <CardContent
                              sx={{
                                // border: '1px solid #DDE1E6',
                                borderRadius: '10px',
                                boxShadow: 'none',
                                p: 0,
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  p: 2,
                                  // borderBottom: '1px solid #DDE1E6',
                                  cursor: 'pointer',
                                }}
                                onClick={() => {
                                  const newExpanded = { ...enablementExpanded };
                                  newExpanded['embedded'] = !newExpanded['embedded'];
                                  setEnablementExpanded(newExpanded);
                                }}
                              >
                                <Typography sx={{ fontWeight: 600, fontSize: '18px' }}>
                                  Enablement Model
                                </Typography>
                              </Box>

                              {/* Embedded Section - Dynamic */}
                              {(() => {
                                const embeddedAgents = normalizedAgentData.embedded || [];

                                // Show section if we're loading OR if there's data
                                if (embeddedAgents.length === 0 && !isIdeationEnablementLoading)
                                  return null;

                                return (
                                  <Box sx={{ borderBottom: '1px solid #DDE1E6' }}>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        p: 2,
                                        cursor: 'pointer',
                                        // bgcolor: enablementExpanded['embedded']
                                        //   ? '#F9FAFB'
                                        //   : 'transparent',
                                      }}
                                      onClick={() => {
                                        const newExpanded = { ...enablementExpanded };
                                        newExpanded['embedded'] = !newExpanded['embedded'];
                                        setEnablementExpanded(newExpanded);
                                      }}
                                    >
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box
                                          component="img"
                                          src="/images/Embedded_icon1.svg"
                                          sx={{ width: 16, height: 16 }}
                                        />
                                        <Typography sx={{ fontWeight: 600, fontSize: '15px' }}>
                                          Embedded
                                        </Typography>
                                      </Box>
                                      <Box
                                        sx={{
                                          transform: enablementExpanded['embedded']
                                            ? 'rotate(180deg)'
                                            : 'rotate(0deg)',
                                          transition: 'transform 0.3s',
                                        }}
                                      >
                                        <KeyboardArrowDownIcon />
                                      </Box>
                                    </Box>

                                    {enablementExpanded['embedded'] && (
                                      <Box sx={{ px: 2, pb: 2 }}>
                                        {isIdeationEnablementLoading ? (
                                          <Box
                                            sx={{
                                              display: 'grid',
                                              gridTemplateColumns: {
                                                xs: '1fr',
                                                md: 'repeat(2, 1fr)',
                                              },
                                              gap: 2,
                                            }}
                                          >
                                            {[1, 2, 3].map((i) => (
                                              <Box
                                                key={i}
                                                sx={{ p: 2, borderRadius: '8px', bgcolor: '#FFF7F3' }}
                                              >
                                                <Skeleton variant="text" width="60%" height={20} />
                                                <Skeleton
                                                  variant="text"
                                                  width="100%"
                                                  height={16}
                                                  sx={{ mt: 1 }}
                                                />
                                                <Skeleton variant="text" width="100%" height={16} />
                                                <Skeleton variant="text" width="80%" height={16} />
                                              </Box>
                                            ))}
                                          </Box>
                                        ) : (
                                          <Box
                                            sx={{
                                              display: 'grid',
                                              gridTemplateColumns: {
                                                xs: '1fr',
                                                md: 'repeat(2, 1fr)',
                                              },
                                              gap: 2,
                                            }}
                                          >
                                            {embeddedAgents.map((agent: CardData, index: number) => (
                                              <Box
                                                key={index}
                                                sx={{
                                                  position: 'relative',
                                                  borderRadius: '8px',
                                                }}
                                              >
                                                {isEditing && (
                                                  <DriveFileRenameOutlineIcon
                                                    sx={{
                                                      position: 'absolute',
                                                      top: 8,
                                                      right: 8,
                                                      cursor: 'pointer',
                                                      height: 16,
                                                      width: 16,
                                                      color: 'grey.500',
                                                      zIndex: 1,
                                                    }}
                                                    onClick={() =>
                                                      handleEdit('embedded', { index: index })
                                                    }
                                                  />
                                                )}

                                                <EnablementCard
                                                  title={agent.capability_name}
                                                  content={agent.description}
                                                  bgcolor="#F6F1FF"
                                                />
                                              </Box>
                                            ))}
                                          </Box>
                                        )}
                                      </Box>
                                    )}
                                  </Box>
                                );
                              })()}

                              {/* Extended Section - Dynamic */}
                              {(() => {
                                const extendedAgents = normalizedAgentData.extended || [];

                                // Show section if we're loading OR if there's data
                                if (extendedAgents.length === 0 && !isIdeationEnablementLoading)
                                  return null;

                                return (
                                  <Box sx={{ borderBottom: '1px solid #DDE1E6' }}>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        p: 2,
                                        cursor: 'pointer',
                                        // bgcolor: enablementExpanded['extended']
                                        //   ? '#F9FAFB'
                                        //   : 'transparent',
                                      }}
                                      onClick={() => {
                                        const newExpanded = { ...enablementExpanded };
                                        newExpanded['extended'] = !newExpanded['extended'];
                                        setEnablementExpanded(newExpanded);
                                      }}
                                    >
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box
                                          component="img"
                                          src="/images/extended_icon.svg"
                                          sx={{ width: 16, height: 16 }}
                                        />
                                        <Typography sx={{ fontWeight: 600, fontSize: '15px' }}>
                                          Extended
                                        </Typography>
                                      </Box>
                                      <Box
                                        sx={{
                                          transform: enablementExpanded['extended']
                                            ? 'rotate(180deg)'
                                            : 'rotate(0deg)',
                                          transition: 'transform 0.3s',
                                        }}
                                      >
                                        <KeyboardArrowDownIcon />
                                      </Box>
                                    </Box>

                                    {enablementExpanded['extended'] && (
                                      <Box sx={{ px: 2, pb: 2 }}>
                                        {isIdeationEnablementLoading ? (
                                          <Box
                                            sx={{
                                              display: 'grid',
                                              gridTemplateColumns: {
                                                xs: '1fr',
                                                md: 'repeat(2, 1fr)',
                                              },
                                              gap: 2,
                                            }}
                                          >
                                            {[1, 2].map((i) => (
                                              <Box
                                                key={i}
                                                sx={{ p: 2, borderRadius: '8px', bgcolor: '#FFFBED' }}
                                              >
                                                <Skeleton variant="text" width="60%" height={20} />
                                                <Skeleton
                                                  variant="text"
                                                  width="100%"
                                                  height={16}
                                                  sx={{ mt: 1 }}
                                                />
                                                <Skeleton variant="text" width="100%" height={16} />
                                                <Skeleton variant="text" width="80%" height={16} />
                                              </Box>
                                            ))}
                                          </Box>
                                        ) : (
                                          <Box
                                            sx={{
                                              display: 'grid',
                                              gridTemplateColumns: {
                                                xs: '1fr',
                                                md: 'repeat(2, 1fr)',
                                              },
                                              gap: 2,
                                            }}
                                          >
                                            {extendedAgents.map((agent: CardData, index: number) => (
                                              <Box
                                                key={index}
                                                sx={{
                                                  position: 'relative',
                                                  borderRadius: '8px',
                                                }}
                                              >
                                                {isEditing && (
                                                  <DriveFileRenameOutlineIcon
                                                    sx={{
                                                      position: 'absolute',
                                                      top: 8,
                                                      right: 8,
                                                      cursor: 'pointer',
                                                      height: 16,
                                                      width: 16,
                                                      color: 'grey.500',
                                                      zIndex: 1,
                                                    }}
                                                    onClick={() =>
                                                      handleEdit('extended', { index: index })
                                                    }
                                                  />
                                                )}
                                                <EnablementCard
                                                  key={index}
                                                  title={agent.capability_name}
                                                  content={agent.description}
                                                  bgcolor="#FFFBED"
                                                />
                                              </Box>
                                            ))}
                                          </Box>
                                        )}
                                      </Box>
                                    )}
                                  </Box>
                                );
                              })()}

                              {/* Custom Section (Client Specific) - Dynamic */}
                              {(() => {
                                const customAgents = normalizedAgentData.custom || [];

                                // Show section if we're loading OR if there's data
                                if (customAgents.length === 0 && !isIdeationEnablementLoading)
                                  return null;

                                return (
                                  <Box>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        p: 2,
                                        cursor: 'pointer',
                                        // bgcolor: enablementExpanded['custom'] ? '#F9FAFB' : 'transparent',
                                      }}
                                      onClick={() => {
                                        const newExpanded = { ...enablementExpanded };
                                        newExpanded['custom'] = !newExpanded['custom'];
                                        setEnablementExpanded(newExpanded);
                                      }}
                                    >
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box
                                          component="img"
                                          src="/images/client_specific.svg"
                                          sx={{ width: 16, height: 16 }}
                                        />
                                        <Typography sx={{ fontWeight: 600, fontSize: '15px' }}>
                                          Custom
                                        </Typography>
                                      </Box>
                                      <Box
                                        sx={{
                                          transform: enablementExpanded['custom']
                                            ? 'rotate(180deg)'
                                            : 'rotate(0deg)',
                                          transition: 'transform 0.3s',
                                        }}
                                      >
                                        <KeyboardArrowDownIcon />
                                      </Box>
                                    </Box>

                                    {enablementExpanded['custom'] && (
                                      <Box sx={{ px: 2, pb: 2 }}>
                                        {isIdeationEnablementLoading ? (
                                          <Box
                                            sx={{
                                              display: 'grid',
                                              gridTemplateColumns: {
                                                xs: '1fr',
                                                md: 'repeat(2, 1fr)',
                                              },
                                              gap: 2,
                                            }}
                                          >
                                            {[1].map((i) => (
                                              <Box
                                                key={i}
                                                sx={{ p: 2, borderRadius: '8px', bgcolor: '#F4FFF2' }}
                                              >
                                                <Skeleton variant="text" width="60%" height={20} />
                                                <Skeleton
                                                  variant="text"
                                                  width="100%"
                                                  height={16}
                                                  sx={{ mt: 1 }}
                                                />
                                                <Skeleton variant="text" width="100%" height={16} />
                                                <Skeleton variant="text" width="80%" height={16} />
                                              </Box>
                                            ))}
                                          </Box>
                                        ) : (
                                          <Box
                                            sx={{
                                              display: 'grid',
                                              gridTemplateColumns: {
                                                xs: '1fr',
                                                md: 'repeat(2, 1fr)',
                                              },
                                              gap: 2,
                                            }}
                                          >
                                            {customAgents.map((agent: CardData, index: number) => (
                                              <Box
                                                key={index}
                                                sx={{
                                                  position: 'relative',
                                                  borderRadius: '8px',
                                                }}
                                              >
                                                {isEditing && (
                                                  <DriveFileRenameOutlineIcon
                                                    sx={{
                                                      position: 'absolute',
                                                      top: 8,
                                                      right: 8,
                                                      cursor: 'pointer',
                                                      height: 16,
                                                      width: 16,
                                                      color: 'grey.500',
                                                      zIndex: 1,
                                                    }}
                                                    onClick={() =>
                                                      handleEdit('custom', { index: index })
                                                    }
                                                  />
                                                )}
                                                <EnablementCard
                                                  key={index}
                                                  title={agent.capability_name}
                                                  content={agent.description}
                                                  bgcolor="#F4FFF2"
                                                />
                                              </Box>
                                            ))}
                                          </Box>
                                        )}
                                      </Box>
                                    )}
                                  </Box>
                                );
                              })()}
                            </CardContent>
                          </Card>

                          {/* Risks and Mitigation Section - Keep static as before */}
                          {(() => {
                            const risksAndMitigation =
                              displayData?.agent?.ai_enablement?.risks_and_mitigation || [];

                            // Show section if we're loading OR if there's data
                            if (risksAndMitigation.length === 0 && !isIdeationEnablementLoading)
                              return null;

                            // return (
                            //   <Card sx={{ mb: 2, boxShadow: 'none' }}>
                            //     <CardContent
                            //       sx={{
                            //         // border: '1px solid #DDE1E6',
                            //         borderRadius: '10px',
                            //         boxShadow: 'none',
                            //         p: 0,
                            //       }}
                            //     >
                            //       <Box
                            //         sx={{
                            //           display: 'flex',
                            //           alignItems: 'center',
                            //           justifyContent: 'space-between',
                            //           px: 2,
                            //           // borderBottom: '1px solid #DDE1E6',
                            //         }}
                            //       >
                            //         <Typography sx={{ fontWeight: 600, fontSize: '18px' }}>
                            //           Risks and Mitigation
                            //         </Typography>
                            //       </Box>
                            //       {isIdeationEnablementLoading ? (
                            //         <Box sx={{ p: 2 }}>
                            //           {[1, 2].map((i) => (
                            //             <Box
                            //               key={i}
                            //               sx={{
                            //                 p: 1,
                            //                 m: 2,
                            //                 border: '1px solid #DDE1E6',
                            //                 borderRadius: '8px',
                            //               }}
                            //             >
                            //               <Box
                            //                 sx={{
                            //                   display: 'flex',
                            //                   alignItems: 'center',
                            //                   justifyContent: 'space-between',
                            //                   position: 'relative',
                            //                   pb: 1,
                            //                   mb: 1,
                            //                 }}
                            //               >
                            //                 <Skeleton variant="text" width="40%" height={24} />
                            //               </Box>
                            //               <Box
                            //                 sx={{
                            //                   display: 'grid',
                            //                   gridTemplateColumns: { xs: '1fr', md: '1fr auto 1fr' },
                            //                   gap: 1,
                            //                   alignItems: 'stretch',
                            //                 }}
                            //               >
                            //                 <Box sx={{ p: 2, borderRadius: '8px', bgcolor: '#FFFFFF' }}>
                            //                   <Skeleton variant="text" width="30%" height={20} />
                            //                   <Skeleton
                            //                     variant="text"
                            //                     width="100%"
                            //                     height={16}
                            //                     sx={{ mt: 1 }}
                            //                   />
                            //                   <Skeleton variant="text" width="100%" height={16} />
                            //                   <Skeleton variant="text" width="80%" height={16} />
                            //                 </Box>
                            //                 <Divider
                            //                   orientation="vertical"
                            //                   flexItem
                            //                   sx={{
                            //                     borderColor: '#DDE1E6',
                            //                   }}
                            //                 />
                            //                 <Box sx={{ p: 2, borderRadius: '8px', bgcolor: '#FFFFFF' }}>
                            //                   <Skeleton variant="text" width="30%" height={20} />
                            //                   <Skeleton
                            //                     variant="text"
                            //                     width="100%"
                            //                     height={16}
                            //                     sx={{ mt: 1 }}
                            //                   />
                            //                   <Skeleton variant="text" width="100%" height={16} />
                            //                   <Skeleton variant="text" width="80%" height={16} />
                            //                 </Box>
                            //               </Box>
                            //             </Box>
                            //           ))}
                            //         </Box>
                            //       ) : (
                            //         risksAndMitigation.map((item: any, index: number) => (
                            //           <Box
                            //             key={index}
                            //             sx={{
                            //               p: 1,
                            //               m: 2,
                            //               border: '1px solid #DDE1E6',
                            //               borderRadius: '8px',
                            //             }}
                            //           >
                            //             <Box
                            //               sx={{
                            //                 display: 'flex',
                            //                 alignItems: 'center',
                            //                 justifyContent: 'space-between',
                            //                 position: 'relative',
                            //                 pb: 1,
                            //               }}
                            //             >
                            //               <Typography
                            //                 sx={{
                            //                   fontWeight: 600,
                            //                   fontSize: '16px',
                            //                   color: '#111827',
                            //                 }}
                            //               >
                            //                 {item?.risk_title || ''}
                            //               </Typography>

                            //               {isEditing && (
                            //                 <DriveFileRenameOutlineIcon
                            //                   sx={{
                            //                     position: 'absolute',
                            //                     top: 8,
                            //                     right: 8,
                            //                     cursor: 'pointer',
                            //                     height: 16,
                            //                     width: 16,
                            //                     color: 'grey.500',
                            //                     zIndex: 1,
                            //                   }}
                            //                   onClick={() => handleEdit('risk', { index: index })}
                            //                 />
                            //               )}
                            //             </Box>

                            //             <Box
                            //               sx={{
                            //                 display: 'grid',
                            //                 gridTemplateColumns: { xs: '1fr', md: '1fr auto 1fr' },
                            //                 gap: 1,
                            //                 alignItems: 'stretch',
                            //               }}
                            //             >
                            //               <EnablementCard
                            //                 title="Overview"
                            //                 content={item?.risk_overview || ''}
                            //                 bgcolor="#FFFFFF"
                            //                 padding="0px"
                            //               />
                            //               <Divider
                            //                 orientation="vertical"
                            //                 flexItem
                            //                 sx={{
                            //                   borderColor: '#DDE1E6',
                            //                 }}
                            //               />

                            //               <EnablementCard
                            //                 title="Mitigation"
                            //                 content={item?.mitigation || ''}
                            //                 bgcolor="#FFFFFF"
                            //                 padding="0px"
                            //               />
                            //             </Box>
                            //           </Box>
                            //         ))
                            //       )}
                            //     </CardContent>
                            //   </Card>
                            // );
                          })()}
                        </>
                      ) : (
                        // Agents view for isIdeation = false
                        <>
                          {displayData?.llm_agent?.map((data: any, index: number) => {
                            let matched: any = undefined;
                            if (Array.isArray(displayData?.agent?.embedded)) {
                              matched = displayData?.agent?.embedded.find(
                                (item: any) => item?.agent_name === data.agent_name
                              );
                            } else if (agentLlmResponse && typeof agentLlmResponse === 'object') {
                              const candidateArray = Array.isArray(
                                (agentLlmResponse as any).agent_classification
                              )
                                ? (agentLlmResponse as any).agent_classification
                                : Object.values(agentLlmResponse as any);
                              if (Array.isArray(candidateArray)) {
                                matched = candidateArray.find(
                                  (item: any) => item?.agent_name === data.agent_name
                                );
                              }
                            }

                            const styleConfig =
                              matched && IMPLEMENTATION_STYLES[matched?.implementation_type];

                            return (
                              <Grid key={index}>
                                <Card sx={{ mb: 3, boxShadow: 'none' }}>
                                  <CardContent
                                    sx={{
                                      border: '1px solid #DDE1E6',
                                      borderRadius: '10px',
                                      boxShadow: 'none',
                                    }}
                                  >
                                    {/* Header */}
                                    <Box sx={{ display: 'flex', alignItems: 'start' }}>
                                      <Box sx={{ display: 'flex', flex: 1, mr: 3 }}>
                                        <img src="/images/agentIcon.svg" alt="agentIcon" />
                                        <Box sx={{ ml: '12px' }}>
                                          <Typography
                                            variant="h6"
                                            sx={{
                                              fontWeight: 600,
                                              fontSize: '12px',
                                              color: '#1B54F8',
                                            }}
                                          >
                                            {data?.agent_enabler?.join(', ')}
                                          </Typography>
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              gap: 1,
                                            }}
                                          >
                                            <Typography
                                              variant="h6"
                                              sx={{ fontWeight: 600, fontSize: '18px' }}
                                            >
                                              {data?.agent_name}
                                            </Typography>
                                            {isEditing && (
                                              <DriveFileRenameOutlineIcon
                                                sx={{
                                                  cursor: 'pointer',
                                                  height: '16px',
                                                  width: '16px',
                                                  color: 'grey.500',
                                                }}
                                                onClick={() =>
                                                  handleEdit('agent_name', {
                                                    index: index,
                                                    name: data?.agent_name,
                                                  })
                                                }
                                              />
                                            )}
                                          </Box>
                                        </Box>
                                      </Box>
                                    </Box>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        gap: '15px',
                                      }}
                                    >
                                      {renderInfoBox({
                                        title: 'Task',
                                        titleColor: '#3286DB',
                                        icon: '/images/taskIcon.svg',
                                        text: data?.agent_description,
                                        bg: '#F7FBFF',
                                        extraSx: {
                                          width:
                                            matched || isLoadingAgentLlmResponse
                                              ? { xs: '100%', sm: '50%' }
                                              : '100%',
                                        },
                                      })}

                                      {isLoadingAgentLlmResponse ? (
                                        <Box
                                          sx={{
                                            borderRadius: '12px',
                                            px: 2,
                                            py: 2,
                                            fontSize: '12px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            background: '#FAFAFA',
                                            gap: 1.5,
                                            mt: '18px',
                                            width: { xs: '100%', sm: '50%' },
                                          }}
                                        >
                                          <Skeleton
                                            variant="rounded"
                                            width={90}
                                            height={10}
                                            animation="wave"
                                            sx={{ borderRadius: '8px' }}
                                          />
                                          <Skeleton
                                            variant="rectangular"
                                            height={10}
                                            width="100%"
                                            animation="wave"
                                            sx={{ borderRadius: '6px', mt: '10px' }}
                                          />
                                          <Skeleton
                                            variant="rectangular"
                                            height={10}
                                            width="100%"
                                            animation="wave"
                                            sx={{ borderRadius: '6px' }}
                                          />
                                          <Skeleton
                                            variant="rectangular"
                                            height={10}
                                            width="100%"
                                            animation="wave"
                                            sx={{ borderRadius: '6px' }}
                                          />
                                        </Box>
                                      ) : (
                                        matched &&
                                        styleConfig &&
                                        renderInfoBox({
                                          title: matched.implementation_type,
                                          titleColor: styleConfig.color,
                                          icon: styleConfig.icon,
                                          text: matched.justification,
                                          bg: styleConfig.bg,
                                          extraSx: {
                                            width: { xs: '100%', sm: '50%' },
                                          },
                                        })
                                      )}
                                    </Box>
                                  </CardContent>
                                </Card>
                              </Grid>
                            );
                          })}
                        </>
                      )}
                    </Box>
                  )}
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TopBenefits
                allTopBenefits={
                  displayData?.benefits
                    ? Object.entries(displayData.benefits).map(([key, value]) => ({
                      title: getBenefitTitle(key),
                      label: getBenefitLabelNew(value as string),
                    }))
                    : []
                }
                isEditing={isEditing}
                solutionData={updatedSolutionData}
                setSolutionData={setUpdatedSolutionData}
              />
            </Grid>

            {/* <Grid size={{ xs: 12, md: 12 }}></Grid> */}

            <ConfirmationModal
              isOpen={showDeleteConfirmModal}
              title={`Delete Opportunity`}
              description={
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography sx={{ color: '#4f4f4f' }}>
                    <Box>
                      Are you sure you want to delete this opportunity? This action cannot be
                      undone. All associated data will be permanently removed.
                    </Box>
                  </Typography>
                </Box>
              }
              primaryButtonText={
                <Box display="flex" alignItems="center" gap={1}>
                  Delete
                </Box>
              }
              onPrimaryAction={() => {
                handleRemoveClick?.();
                setShowDeleteConfirmModal(false);
              }}
              onClose={() => setShowDeleteConfirmModal(false)}
              width={'781px'}
              outerPadding={0}
              sx={effectiveIsIdeation ? { zIndex: 1500 } : undefined}
            />

            <Modal
              isOpen={showFavoriteModal}
              onClose={() => setShowFavoriteModal(false)}
              title={
                isFavorite ? 'Remove AI Solution from Favorites' : 'Add AI Solution to Favorites'
              }
              maxWidth="sm"
              height="md"
            >
              <Box sx={{ bgcolor: '#F9FBFF', borderRadius: '12px', p: 3 }}>
                <Stack spacing={2} alignItems="center">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isFavorite ? (
                      <Star sx={{ color: '#2054F4' }} />
                    ) : (
                      <StarBorder sx={{ color: '#2054F4' }} />
                    )}
                    <Typography sx={{ fontSize: '16px', color: '#2c2c2c' }}>
                      Are you sure you want to{' '}
                      {isFavorite ? 'remove this AI Solution from' : 'add this AI Solution to'} your
                      favorites?
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    onClick={handleToggleFavorite}
                    sx={{
                      textTransform: 'none',
                      bgcolor: '#2054F4',
                      borderRadius: '6px',
                      px: 3,
                      py: 1,
                      '&:hover': { bgcolor: '#1A45C6' },
                      fontWeight: 'bold',
                    }}
                    startIcon={<Add />}
                  >
                    {isFavorite ? 'Remove' : 'Add'}
                  </Button>
                </Stack>
              </Box>
            </Modal>
            <Snackbar
              open={showApplySuccessSnackbar}
              autoHideDuration={3000}
              onClose={() => setShowApplySuccessSnackbar(false)}
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
              <Alert
                onClose={() => setShowApplySuccessSnackbar(false)}
                severity="success"
                sx={{ width: '100%' }}
              >
                AI Solution applied successfully
              </Alert>
            </Snackbar>
          </Grid>
          {openEditModal && (
            <SolutionEditModal
              isOpen={openEditModal}
              solutionData={updatedSolutionData}
              setSolutionData={setUpdatedSolutionData}
              onSave={handleSaveSolution}
              onClose={() => setOpenEditModal(false)}
              type={editType as EditType}
              editItem={editItem || undefined}
              setLlmResponseData={setLlmResponseData}
            />
          )}
        </Box>
      </Box>
    </>
  );
};

export default ViewSolutionDetails;
